import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Student, Dimension } from '../types';
import { useTenant } from '../contexts/TenantContext';
import { getCourseDetailedOrder } from '../utils/courseUtils';
import SimpleTutorialSection from './SimpleTutorialSection';
import {
  ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Scatter, ZAxis, Legend, Rectangle
} from 'recharts';
import StudentDimensionsChart from './shared/StudentDimensionsChart';
import { FaUserGraduate, FaChalkboardTeacher, FaFileExcel, FaFilePdf, FaFileExport, FaUser, FaEye, FaDownload, FaChartBar, FaInfoCircle, FaCalendarAlt, FaUsers, FaGraduationCap, FaChevronRight, FaChevronLeft, FaChevronDown, FaSpinner, FaCheck, FaFolderOpen, FaTimes, FaPause, FaPlay, FaStopCircle, FaClock } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { filterDimensionsByCourse } from '../utils/dimensionFilters';
import { findStudentById, studentIdsMatch } from '../utils/studentIdUtils';
import { getVisibleSubdimensionDescriptionsForPdf } from '../utils/getVisibleSubdimensionDescriptions';
import { renderMethodologicalNoteTable } from '../utils/pdfTextSectionRenderer';
import { autoconcienciaVoluntadDimension } from './AutoconcienciaVoluntad';
import { habilidadesPensamientoDimension } from './HabilidadesPensamiento';
import { habilidadesComunicacionalesDimension } from './HabilidadesComunicacionales';
import { corporalidadMovimientoDimension } from './CorporalidadyMovimiento';
import { lenguajesArtisticosDimension } from './LenguajeArtistico';
import { motivacionAprendizajeDimension } from './Motivacionaprendizaje';
import { encuentroNaturalezaDimension } from './Encuentronaturaleza';
import { habilidadesSocialesDimension } from './Habilidadessociales';
import { pensamientoMatematicosDimension } from './Pensamientomatematico';
import { lenguaInglesaDimension } from './LenguaInglesa';
import { amorDiosDimension } from './AmordeDios';
// Import imágenes desde assets para el PDF
import logoImage from '../assets/images/logo.png';
import schoolIllustration from '../assets/images/school-illustration.png';
import childrenImage from '../assets/images/drawing_of_boy_and_girl_v3.png';
import olderStudentsImage from '../assets/images/Foto_informe_1ro_hacia_arriba.jpg';
// Importar token manager para renovación automática
import { tokenManager } from '../utils/tokenManager';
// Utilidades centralizadas para cálculo de niveles y ejes
import {
  deriveAnchoredAge,
  getSixYearRangeForAge,
  createAgeTicksAndLabels,
  chartOffsetForMinAge,
  calculateExpectedPercentage,
  calculatePreviousYearPercentage,
  BASE_MIN_AGE,
  BASE_MAX_AGE,
  COURSE_TO_AGE_MAP,
} from '../utils/levelCalculations';

// Importar funciones para cálculos correctos excluyendo "No aplica"
import {
  calculateDimensionStatistics,
  getValidLevelsCount,
  getValidLevelsAchieved,
  isValidSkillLevel,
  isNoAplicaLevelSelected,
  calculateProgressByAgeTramos
} from '../utils/gradeCalculationForWithdrawal';

// Declaración para garbage collection (disponible en Chrome DevTools)
declare global {
  interface Window {
    gc?: () => void;
  }
}

// Función auxiliar para extraer RUT del estudiante
const extractRutFromStudent = (student: Student): string => {
  // PRIORIDAD 1: Si el estudiante tiene originalId (contiene RUT completo con K)
  if ((student as any).originalId) {
    const originalId = String((student as any).originalId);
    // Verificar que sea un RUT válido con K
    if (/^[0-9]{7,8}[0-9kK]$/i.test(originalId)) {
      return originalId.replace(/\./g, '').replace(/-/g, '');
    }
  }
  
  // PRIORIDAD 2: Si el estudiante tiene metadata.rut
  if ((student as any).metadata?.rut) {
    return (student as any).metadata.rut.replace(/\./g, '').replace(/-/g, '');
  }
  
  // PRIORIDAD 3: Convertir ID a string para trabajar con él
  const studentIdStr = String(student.id);
  
  // Si el RUT está en el ID como string con formato válido
  if (/^[0-9]{7,8}[0-9kK]$/i.test(studentIdStr)) {
    return studentIdStr.replace(/\./g, '').replace(/-/g, '');
  }
  
  // PRIORIDAD 4: Intentar extraer RUT del nombre
  const nameMatch = student.name.match(/\b[0-9]{1,2}\.[0-9]{3}\.[0-9]{3}-[0-9kK]\b/);
  if (nameMatch) {
    return nameMatch[0].replace(/\./g, '').replace(/-/g, '');
  }
  
  // ÚLTIMO RECURSO: usar el ID convertido a string
  return studentIdStr;
};

// Función utilitaria para obtener el logo correcto según el tenant
const getTenantLogo = () => {
  console.log('🔍 getTenantLogo - Obteniendo logo del tenant...');
  
  const tenantKey = localStorage.getItem('tenantKey');
  console.log('🏢 TenantKey actual:', tenantKey);
  
  // PRIMERO: Intentar obtener datos de organización desde localStorage
  const organizationData = localStorage.getItem('organizationData');
  console.log('📦 organizationData en localStorage:', organizationData ? 'Encontrado' : 'No encontrado');
  
  if (organizationData) {
    try {
      const orgData = JSON.parse(organizationData);
      console.log('📋 Datos de organización parseados:');
      console.log('   - tenantKey:', orgData.tenantKey);
      console.log('   - displayName:', orgData.displayName);
      console.log('   - logoBase64:', orgData.branding?.logoBase64 ? `✅ Presente (${orgData.branding.logoBase64.substring(0, 50)}...)` : '❌ No definido');
      console.log('   - logoUrl:', orgData.branding?.logoUrl);
      
      // PRIORIDAD 1: Logo en base64 (subido directamente por el admin)
      if (orgData.branding?.logoBase64) {
        console.log('🖼️ ✅ Usando logo base64 para tenant:', orgData.tenantKey);
        return orgData.branding.logoBase64;
      }
      
      // PRIORIDAD 2: Logo URL configurado
      if (orgData.branding?.logoUrl) {
        console.log('🖼️ ✅ Logo URL encontrado:', orgData.branding.logoUrl);
        
        // Para CPV, usar logo importado directamente para garantizar que funcione
        if (tenantKey === 'cpv_cl' && orgData.branding.logoUrl.includes('logocolegiosinnotas')) {
          console.log('🔬 CPV detectado - Usando logo importado para producción');
          return logoImage;
        }
        
        // Si ya es una URL absoluta o base64, usarla directamente
        if (orgData.branding.logoUrl.startsWith('http') || orgData.branding.logoUrl.startsWith('data:')) {
          return orgData.branding.logoUrl;
        } else {
          // Construir URL completa para producción
          const baseUrl = process.env.PUBLIC_URL || window.location.origin;
          return `${baseUrl}${orgData.branding.logoUrl}`;
        }
      }
    } catch (e) {
      console.error('❌ Error parseando organizationData:', e);
    }
  }

  // FALLBACK: Intentar por tenantKey
  const userData = localStorage.getItem('userData');
  
  if (userData) {
    try {
      const user = JSON.parse(userData);
      const tenantKey = user.tenantKey || localStorage.getItem('tenantKey');
      
      // Para Colegio Puerto Varas usar logo importado (funciona en producción)
      if (tenantKey === 'cpv_cl') {
        console.log('🔬 Fallback CPV - Usando logo importado');
        return logoImage;
      }
    } catch (e) {
      console.error('❌ Error parsing userData:', e);
    }
  }
  
  // ÚLTIMO FALLBACK - usar logo importado para producción
  console.log('🔄 Usando fallback por defecto - logo importado');
  return logoImage;
};

// 🎯 Función para determinar si un curso es de nivel básico (1ro básico hacia arriba)
const isBasicLevelCourse = (course: string): boolean => {
  if (!course) return false;

  // Convertir a minúsculas para comparación case-insensitive
  const courseLower = course.toLowerCase();

  // Patrones para identificar cursos básicos (1ro a 6to básico)
  const basicPatterns = [
    // Formato "Primero básico", "Segundo básico", etc.
    /^primero\s+básico/,
    /^segundo\s+básico/,
    /^tercero\s+básico/,
    /^cuarto\s+básico/,
    /^quinto\s+básico/,
    /^sexto\s+básico/,
    // Formato "1° básico", "2° básico", etc.
    /^1°?\s*básico/,
    /^2°?\s*básico/,
    /^3°?\s*básico/,
    /^4°?\s*básico/,
    /^5°?\s*básico/,
    /^6°?\s*básico/,
    // Formato numérico simple
    /^[1-6]\s*básico/
  ];

  // Verificar si el curso coincide con algún patrón básico
  return basicPatterns.some(pattern => pattern.test(courseLower));
};

// Función global para obtener la descripción cualitativa del estudiante
const getQualitativeDescription = (student: Student): string => {
  // Si el estudiante tiene una descripción cualitativa válida, la retornamos
  if (student.qualitativeDescription && student.qualitativeDescription.trim().length > 0) {
    return student.qualitativeDescription.trim();
  }
  // Si no tiene descripción, retornamos vacío (no texto hardcodeado)
  return '';
};

// 🖼️ Función para obtener imagen de portada según el nivel del estudiante
const getChildrenImage = (studentCourse?: string): string => {
  console.log('🔍 DEBUGGING getChildrenImage - Iniciando...', { studentCourse });
  
  // Determinar qué imagen usar según el curso
  const useOlderStudentsImage = studentCourse && isBasicLevelCourse(studentCourse);
  console.log('🎯 Usando imagen para estudiantes mayores:', useOlderStudentsImage);
  
  // Para CPV, usar las imágenes importadas como fallback seguro
  const tenantKey = localStorage.getItem('tenantKey');
  if (tenantKey === 'cpv_cl') {
    console.log('🔬 CPV detectado - Usando imagen importada para producción');
    return useOlderStudentsImage ? olderStudentsImage : childrenImage;
  }
  
  // Construir URL con fallback robusto para otros tenants
  const baseUrl = process.env.PUBLIC_URL || window.location.origin;
  if (useOlderStudentsImage) {
    const olderStudentsPath = `${baseUrl}/images/Foto_informe_1ro_hacia_arriba.jpg`;
    console.log('🔬 Older students image path construida:', olderStudentsPath);
    return olderStudentsPath;
  } else {
    const childrenPath = `${baseUrl}/images/drawing_of_boy_and_girl_v3.png`;
    console.log('🔬 Children image path construida:', childrenPath);
    return childrenPath;
  }
};

  // 🖼️ Función utilitaria para cargar imágenes como base64 para jsPDF
  const loadImageAsBase64 = async (imagePath: string): Promise<string> => {
    // Si ya es base64, devolverlo directamente (logo subido por admin)
    if (imagePath.startsWith('data:image')) {
      return imagePath;
    }
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Para evitar problemas de CORS
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('No se pudo obtener contexto del canvas'));
            return;
          }
          
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          // Convertir a base64
          const base64 = canvas.toDataURL('image/png');
          resolve(base64);
        } catch (error) {
          console.error('Error procesando imagen:', error);
          reject(error);
        }
      };
      
      img.onerror = () => {
        console.error('Error cargando imagen:', imagePath);
        reject(new Error(`No se pudo cargar la imagen: ${imagePath}`));
      };
      
      img.src = imagePath;
    });
  };

  // Función utilitaria para obtener colores de branding personalizados
  const getTenantBrandingColors = () => {
    // Colores por defecto (CPV)
    const defaultColors = {
      primary: { r: 25, g: 44, b: 84 }, // Azul marino de CPV
      secondary: { r: 246, g: 206, b: 30 }, // Amarillo dorado de CPV
      useBranding: false
    };

    try {
      const organizationData = localStorage.getItem('organizationData');
      console.log('🎨 getTenantBrandingColors - organizationData:', organizationData ? 'Encontrado' : 'No encontrado');
      
      if (organizationData) {
        const orgData = JSON.parse(organizationData);
        const tenantKey = orgData.tenantKey || localStorage.getItem('tenantKey');
        
        console.log('🎨 Branding colors config:', orgData.branding?.colors);
        
        // Verificar si hay colores personalizados en branding.colors
        if (orgData.branding?.colors?.primary) {
          const primaryHex = orgData.branding.colors.primary;
          const secondaryHex = orgData.branding.colors.secondary || primaryHex;
          
          console.log('🎨 ✅ Usando colores personalizados:', primaryHex, secondaryHex);
          
          return {
            primary: hexToRgbViz(primaryHex) || defaultColors.primary,
            secondary: hexToRgbViz(secondaryHex) || defaultColors.secondary,
            useBranding: true
          };
        }
      }
    } catch (e) {
      console.error('Error obteniendo colores de branding:', e);
    }
    
    console.log('🎨 Usando colores por defecto');
    return defaultColors;
  };

  // Función para obtener configuración de tamaño de logo
  const getTenantLogoSize = () => {
    const defaultSizes = {
      cover: { width: 48, height: 57.6 }, // Tamaño por defecto para portada
      header: { width: 24, height: 28.8 } // Tamaño por defecto para headers
    };

    try {
      const organizationData = localStorage.getItem('organizationData');
      if (organizationData) {
        const orgData = JSON.parse(organizationData);
        
        if (orgData.branding?.logoSize) {
          return {
            cover: {
              width: orgData.branding.logoSize.coverWidth || defaultSizes.cover.width,
              height: (orgData.branding.logoSize.coverWidth || defaultSizes.cover.width) * 1.2
            },
            header: {
              width: orgData.branding.logoSize.headerWidth || defaultSizes.header.width,
              height: (orgData.branding.logoSize.headerWidth || defaultSizes.header.width) * 1.2
            }
          };
        }
      }
    } catch (e) {
      console.error('Error obteniendo tamaño de logo:', e);
    }
    
    return defaultSizes;
  };

// Función utilitaria para convertir hex a RGB (Visualización)
const hexToRgbViz = (hex: string) => {
  // Remover el # si existe
  const cleanHex = hex.replace('#', '');
  
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      return { r, g, b };
    }
  } else if (cleanHex.length === 3) {
    const r = parseInt(cleanHex.substring(0, 1) + cleanHex.substring(0, 1), 16);
    const g = parseInt(cleanHex.substring(1, 2) + cleanHex.substring(1, 2), 16);
    const b = parseInt(cleanHex.substring(2, 3) + cleanHex.substring(2, 3), 16);
    
    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
      return { r, g, b };
    }
  }
  
  return null;
};

interface Props {
  students: Student[];
  exportToExcel: (data?: any[]) => void;
  exportToPDF: (student: Student, chartImage?: string) => void;
  exportPDFMassive: (students: Student[]) => void;
  onSectionChange?: (section: string) => void;
  onDimensionSelect?: (dimensionId: number) => void;
  onViewModeChange?: (mode: 'student' | 'skill') => void;
}

const COLORS = {
  achieved: '#4A90E2',
  achievedLight: '#B3D4FC',
  expected: '#F5A623',
  expectedLight: '#FFE4B5',
  previousYear: '#6B7280'  // Gris opaco para diferenciarlo
};

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  
  // Usar todo el ancho disponible hasta el final del área del gráfico
  const maxWidth = 1000; // Reducido de 1150 a 1000 para dejar mucho más margen derecho
  const remainingWidth = maxWidth - x - width;
  const arrowSize = 15;

  return (
    <g>
      <path
        d={`
          M ${x + width} ${y}
          L ${x + width + remainingWidth - arrowSize} ${y}
          L ${x + width + remainingWidth} ${y + height / 2}
          L ${x + width + remainingWidth - arrowSize} ${y + height}
          L ${x + width} ${y + height}
          Z
        `}
        fill={fill === COLORS.expected ? COLORS.expectedLight : COLORS.achievedLight}
      />
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        radius={2}
      />
    </g>
  );
};

const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill, width = 4, height = 40 } = props;
  return (
    <Rectangle
      x={cx - 1}  // Centramos el rectángulo en el punto de datos
      y={cy - height / 2}  // Centramos verticalmente el rectángulo
      width={width}   // Ancho del rectángulo
      height={height} // Altura del rectángulo (ajustable según el caso)
      fill={fill}
    />
  );
  };
  

  
  const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border shadow-lg rounded">
        <p className="font-bold">{`${label}`}</p>
        {payload.map((entry: any, index: number) => {
          if (entry && entry.value !== undefined) {
            return (
              <p key={index} style={{ color: entry.fill }}>
                {`${entry.name}: ${entry.value === null ? 'No evaluado' : `${entry.value.toFixed(2)}%`}`}
              </p>
            );
          }
          return null;
        })}
      </div>
    );
  }
  return null;
};

const calculatePercentageAchieved = (dimension: Dimension) => {
  const totalSkills = dimension.skills.length;
  const totalPercentage = dimension.skills.reduce((sum, skill) => {
    return sum + (skill.selectedLevelIndex === null ? 0 : (skill.selectedLevelIndex + 1) * 20);
  }, 0);
  
  return totalPercentage / totalSkills;
};

const calculateExpectedLevel = (courseOrGrade: string) => {
  // NIVEL ESPERADO: Mapeo por curso hacia el nivel esperado (edad + 1)
  const gradeMap: { [key: string]: number } = {
    'Medio Mayor': 40,      // Hacia 4 años (Pre Kinder)
    'Pre Kinder': 60,       // Hacia 5 años (Kinder)
    'Kinder': 80,           // Hacia 6 años (1° Básico)
    'Primero básico': 100,  // Hacia 7 años (2° Básico)
    'Segundo básico': 100,  // Hacia 8+ años (3° Básico en adelante)
    'Tercero básico': 80,   // Corregido a 80% como en EnhancedPedagogicalAnalytics
    'Cuarto básico': 100,   // Hacia 11+ años (5° Básico en adelante)
    'Quinto básico': 100,   // Hacia 12+ años (6° Básico)
    'Sexto básico': 100     // Máximo nivel alcanzado
  };
  
  // Extraer el grado del curso (ej: "Primero básico A" -> "Primero básico")
  const grade = courseOrGrade.replace(/\s[A-Z]$/, '');
  return gradeMap[grade] || 0;
};

const calculateLastYearPercentage = (courseOrGrade: string) => {
  // NIVEL ANTERIOR: Mapeo por curso hacia el nivel actual (edad actual)
  const gradeMap: { [key: string]: number } = {
    'Medio Mayor': 20,      // 3 años actual
    'Pre Kinder': 40,       // 4 años actual
    'Kinder': 60,           // 5 años actual
    'Primero básico': 80,   // 6 años actual
    'Segundo básico': 100,  // 7+ años actual
    'Tercero básico': 70,   // Corregido a 70% como en EnhancedPedagogicalAnalytics
    'Cuarto básico': 100,   // 10+ años actual
    'Quinto básico': 100,   // 11+ años actual
    'Sexto básico': 100     // 12+ años actual
  };
  
  // Extraer el grado del curso (ej: "Primero básico A" -> "Primero básico")
  const grade = courseOrGrade.replace(/\s[A-Z]$/, '');
  return gradeMap[grade] || 0;
};

// Añadir esta interfaz cerca del inicio del archivo
interface TeacherInfo {
  course: string;
  spanishTeacher: string;
  englishTeacher: string;
}

// Función actualizada con los docentes 2025 basada en 'docentesporcurso.md'
const getTeacherInfo = (course: string): TeacherInfo | undefined => {
  // Información actualizada de docentes 2025 oficial
  const teachersInfoCurrent = [
    // Nivel Medio Mayor (basado en docentesporcurso.md)
    { course: "MMA", spanishTeacher: "Josefa Crovetto", englishTeacher: "Valentina Thienemann" },
    { course: "MMB", spanishTeacher: "Rossana Díaz", englishTeacher: "Nicole Larrea" },
    { course: "MMC", spanishTeacher: "Bernardita Suazo", englishTeacher: "María Jesús Valdivieso" },
    { course: "MMD", spanishTeacher: "María Jesús Jerez", englishTeacher: "Francisca Portaluppi" },
    // Prekínder
    { course: "PKA", spanishTeacher: "Camila Oyarzo", englishTeacher: "Teresa Echeverría" },
    { course: "PKB", spanishTeacher: "Isabel Rebolledo", englishTeacher: "Pilar Plaza de los Reyes" },
    { course: "PKC", spanishTeacher: "Carola Meersohn", englishTeacher: "Marcela Moreno" },
    // Kínder
    { course: "KA", spanishTeacher: "Magdalena Undurraga", englishTeacher: "Camila Álvarez" },
    { course: "KB", spanishTeacher: "Constanza Moguillansky", englishTeacher: "Carolina Krause" },
    { course: "KC", spanishTeacher: "Isidora Barrientos", englishTeacher: "Camila Mejías" },
    // Primero Básico
    { course: "1A", spanishTeacher: "Paula Larraín", englishTeacher: "Josefina Durán" },
    { course: "1B", spanishTeacher: "Danitza Munjin", englishTeacher: "Rebeca Pizarro" },
    { course: "1C", spanishTeacher: "Tamara Torres", englishTeacher: "María Carolina Alfaro" },
    // Segundo Básico
    { course: "2A", spanishTeacher: "Dolores Ugalde", englishTeacher: "Daniela Herrera" },
    { course: "2B", spanishTeacher: "Magdalena Izquierdo", englishTeacher: "María Luisa Gómez" },
    { course: "2C", spanishTeacher: "Soledad Parada", englishTeacher: "Magdalena Latorre" },
    // Tercero Básico
    { course: "3A", spanishTeacher: "Bernardita Montero", englishTeacher: "Bernardita Montero" },
    { course: "3B", spanishTeacher: "Macarena Gutiérrez", englishTeacher: "Macarena Gutiérrez" },
    { course: "3C", spanishTeacher: "Javiera Montero", englishTeacher: "Javiera Montero" },
    // Cuarto Básico (NUEVO)
    { course: "4A", spanishTeacher: "Claudia Uribe", englishTeacher: "Claudia Uribe" },
    { course: "4B", spanishTeacher: "Alejandra Ceza", englishTeacher: "Alejandra Ceza" },
    { course: "4C", spanishTeacher: "Andrea Infante", englishTeacher: "Andrea Infante" },
    // Quinto Básico (NUEVO)
    { course: "5A", spanishTeacher: "Sara Barra", englishTeacher: "Sara Barra" },
    { course: "5B", spanishTeacher: "Gloria Rivera", englishTeacher: "Gloria Rivera" },
    { course: "5C", spanishTeacher: "Daniela Sandoval", englishTeacher: "Daniela Sandoval" }
  ];

  const courseMap: { [key: string]: string } = {
    // Variaciones de Medio Mayor
    'Nivel medio mayor A': 'MMA',
    'Nivel medio mayor B': 'MMB',
    'Nivel medio mayor C': 'MMC',
    'Nivel medio mayor D': 'MMD',
    'Medio Mayor A': 'MMA',
    'Medio Mayor B': 'MMB',
    'Medio Mayor C': 'MMC',
    'Medio Mayor D': 'MMD',
    // Prekínder
    'Prekínder A': 'PKA',
    'Prekínder B': 'PKB',
    'Prekínder C': 'PKC',
    'Pre Kinder A': 'PKA',
    'Pre Kinder B': 'PKB',
    'Pre Kinder C': 'PKC',
    // Kínder
    'Kínder A': 'KA',
    'Kínder B': 'KB',
    'Kínder C': 'KC',
    'Kinder A': 'KA',
    'Kinder B': 'KB',
    'Kinder C': 'KC',
    // Básico
    'Primero básico A': '1A',
    'Primero básico B': '1B',
    'Primero básico C': '1C',
    '1° básico A': '1A',
    '1° básico B': '1B',
    '1° básico C': '1C',
    'Segundo básico A': '2A',
    'Segundo básico B': '2B',
    'Segundo básico C': '2C',
    '2° básico A': '2A',
    '2° básico B': '2B',
    '2° básico C': '2C',
    'Tercero básico A': '3A',
    'Tercero básico B': '3B',
    'Tercero básico C': '3C',
    '3° básico A': '3A',
    '3° básico B': '3B',
    '3° básico C': '3C',
    'Cuarto básico A': '4A',
    'Cuarto básico B': '4B',
    'Cuarto básico C': '4C',
    '4° básico A': '4A',
    '4° básico B': '4B',
    '4° básico C': '4C',
    'Quinto básico A': '5A',
    'Quinto básico B': '5B',
    'Quinto básico C': '5C',
    '5° básico A': '5A',
    '5° básico B': '5B',
    '5° básico C': '5C'
  };

  const courseCode = courseMap[course];
  return teachersInfoCurrent.find(info => info.course === courseCode);
};

// Agregar esta función antes de wrapText
const getTextWidth = (text: string): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = '700 16px Inter, Arial, sans-serif'; // ✅ FUENTE PROFESIONAL ACTUALIZADA
    return context.measureText(text).width;
  }
  return text.length * 9; // ✅ FALLBACK mejorado para fuente profesional
};

// Modificar la función wrapText para forzar máximo 2 líneas
const wrapText = (text: string, maxWidth: number): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = getTextWidth(currentLine + " " + word);
    
    if (width < maxWidth) {
      currentLine += " " + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
    
    // Si es la última palabra y aún no se ha agregado la línea
    if (i === words.length - 1) {
      lines.push(currentLine);
    }
  }

  // Si solo hay una palabra, agregarla como única línea
  if (lines.length === 0 && currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

// Las imágenes se importan directamente desde assets

// Función utilitaria robusta para parsear fechas en múltiples formatos
const parseDate = (birthDate: string): Date | null => {
  if (!birthDate) return null;
  
  const dateStr = birthDate.trim();
  
  // Formato YYYY-MM-DD
  if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) return date;
  }
  
  // Formato DD-MM-YYYY o DD-MM-YY
  if (dateStr.match(/^\d{1,2}-\d{1,2}-\d{2,4}$/)) {
    const [day, month, yearStr] = dateStr.split('-').map(num => parseInt(num));
    
    // Convertir año de 2 dígitos a 4 dígitos
    let year = yearStr;
    if (yearStr < 100) {
      // Si el año es <= 25, asume que es 20XX, si es > 25, asume 19XX
      year = yearStr <= 25 ? 2000 + yearStr : 1900 + yearStr;
    }
    
    const date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime()) && date.getFullYear() === year) return date;
  }
  
  // Formato MM/DD/YYYY o similar
  const fallbackDate = new Date(dateStr);
  if (!isNaN(fallbackDate.getTime())) return fallbackDate;
  
  return null;
};

// Función para calcular la edad de manera robusta
const calculateAge = (birthDate: string): string => {
  const birth = parseDate(birthDate);
  if (!birth) return "No disponible";
  
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  // Ajustar la edad si aún no ha cumplido años este año
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  // Validar que la edad sea razonable (entre 0 y 120 años)
  if (age < 0 || age > 120) {
    console.warn(`⚠️ Edad calculada inválida: ${age} años para fecha: ${birthDate}`);
    return "No disponible";
  }
  
  // Calcular los meses
  let months = monthDiff;
  if (months < 0) {
    months += 12;
  }
  
  // Formatear el resultado
  if (age === 0) {
    return `${months} meses`;
  } else {
    return `${age} años${months > 0 ? ` y ${months} meses` : ''}`;
  }
};

// Función para obtener información de docentes (compatibilidad con VisualizacionResultados)
interface TeacherInfoViz {
  spanishTeacher: string;
  englishTeacher: string;
  additionalTeachers?: Array<{ name: string; role: string }>;
}

// Cache global para docentes en visualización
let teachersCacheViz: Array<{ firstName: string; lastName: string; allowedCourses: string[]; role: string }> | null = null;
let teachersCacheTimestampViz = 0;
const CACHE_DURATION_VIZ = 30 * 1000; // ✅ REDUCIDO: 30 segundos en lugar de 5 minutos para cambios más rápidos

// Función para invalidar el cache (puede ser llamada cuando se actualizan los datos)
export const invalidateTeachersCacheViz = () => {
  console.log('🔄 Cache de docentes invalidado');
  teachersCacheViz = null;
  teachersCacheTimestampViz = 0;
};

// Función para obtener docentes desde la API (para VisualizacionResultados)
const fetchTeachersForVisualization = async (): Promise<Array<{ firstName: string; lastName: string; allowedCourses: string[]; role: string }>> => {
  try {
    // Verificar cache
    if (teachersCacheViz && Date.now() - teachersCacheTimestampViz < CACHE_DURATION_VIZ) {
      console.log('📋 Usando cache de docentes (Visualización)');
      return teachersCacheViz;
    }

    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.warn('⚠️ No hay token de autenticación para obtener docentes');
      return [];
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
    const response = await fetch(`${API_BASE_URL}/api/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (result.success && result.data) {
      // Filtrar solo docentes (teachers)
      const teachers = result.data.filter((user: any) => user.role === 'teacher');
      
      // Actualizar cache
      teachersCacheViz = teachers.map((teacher: any) => ({
        firstName: teacher.firstName,
        lastName: teacher.lastName,
        allowedCourses: teacher.allowedCourses || [],
        role: teacher.role
      }));
      teachersCacheTimestampViz = Date.now();
      return teachersCacheViz;
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error obteniendo docentes:', error);
    return [];
  }
};

const getTeacherInfoForVisualization = async (course: string): Promise<TeacherInfoViz | undefined> => {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      return getTeacherInfoHardcodedViz(course);
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

    const apiUrl = `${API_BASE_URL}/api/teacher-reports/settings/${encodeURIComponent(course)}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data) {
        return {
          spanishTeacher: result.data.spanishTeacher,
          englishTeacher: result.data.englishTeacher || '',
          additionalTeachers: result.data.additionalTeachers || []
        };
      }
    }

    return getTeacherInfoHardcodedViz(course);
    
  } catch (error) {
    console.error('❌ Error obteniendo información de docentes:', error);
    return getTeacherInfoHardcodedViz(course);
  }
};

// Función fallback con datos actualizados 2025 para CPV (Visualización)
const getTeacherInfoHardcodedViz = (course: string): TeacherInfoViz | undefined => {
  const teachersInfoCurrent = [
    // Nivel Medio Mayor
    { course: "Nivel medio mayor A", spanishTeacher: "Josefa Crovetto", englishTeacher: "Valentina Thienemann" },
    { course: "Nivel medio mayor B", spanishTeacher: "Rossana Díaz", englishTeacher: "Nicole Larrea" },
    { course: "Nivel medio mayor C", spanishTeacher: "Bernardita Suazo", englishTeacher: "María Jesús Valdivieso" },
    { course: "Nivel medio mayor D", spanishTeacher: "María Jesús Jerez", englishTeacher: "Francisca Portaluppi" },
    // Prekínder
    { course: "Prekínder A", spanishTeacher: "Camila Oyarzo", englishTeacher: "Teresa Echeverría" },
    { course: "Prekínder B", spanishTeacher: "Isabel Rebolledo", englishTeacher: "Pilar Plaza de los Reyes" },
    { course: "Prekínder C", spanishTeacher: "Carola Meersohn", englishTeacher: "Marcela Moreno" },
    // Kínder
    { course: "Kínder A", spanishTeacher: "Magdalena Undurraga", englishTeacher: "Camila Álvarez" },
    { course: "Kínder B", spanishTeacher: "Constanza Moguillansky", englishTeacher: "Carolina Krause" },
    { course: "Kínder C", spanishTeacher: "Isidora Barrientos", englishTeacher: "Camila Mejías" },
    // Primero Básico
    { course: "Primero básico A", spanishTeacher: "Paula Larraín", englishTeacher: "Josefina Durán" },
    { course: "Primero básico B", spanishTeacher: "Danitza Munjin", englishTeacher: "Rebeca Pizarro" },
    { course: "Primero básico C", spanishTeacher: "Tamara Torres", englishTeacher: "María Carolina Alfaro" },
    // Segundo Básico
    { course: "Segundo básico A", spanishTeacher: "Dolores Ugalde", englishTeacher: "Daniela Herrera" },
    { course: "Segundo básico B", spanishTeacher: "Magdalena Izquierdo", englishTeacher: "María Luisa Gómez" },
    { course: "Segundo básico C", spanishTeacher: "Soledad Parada", englishTeacher: "Magdalena Latorre" },
    // Tercero Básico
    { course: "Tercero básico A", spanishTeacher: "Bernardita Montero", englishTeacher: "Bernardita Montero" },
    { course: "Tercero básico B", spanishTeacher: "Macarena Gutiérrez", englishTeacher: "Macarena Gutiérrez" },
    { course: "Tercero básico C", spanishTeacher: "Javiera Montero", englishTeacher: "Javiera Montero" },
    // Cuarto Básico
    { course: "Cuarto básico A", spanishTeacher: "Claudia Uribe", englishTeacher: "Claudia Uribe" },
    { course: "Cuarto básico B", spanishTeacher: "Alejandra Ceza", englishTeacher: "Alejandra Ceza" },
    { course: "Cuarto básico C", spanishTeacher: "Andrea Infante", englishTeacher: "Andrea Infante" },
    // Quinto Básico
    { course: "Quinto básico A", spanishTeacher: "Sara Barra", englishTeacher: "Sara Barra" },
    { course: "Quinto básico B", spanishTeacher: "Gloria Rivera", englishTeacher: "Gloria Rivera" },
    { course: "Quinto básico C", spanishTeacher: "Daniela Sandoval", englishTeacher: "Daniela Sandoval" }
  ];

  const courseMap: { [key: string]: string } = {
    // Variaciones de Nivel Medio Mayor (actualizado para incluir "Nivel medio mayor")
    'Nivel medio mayor A': 'Nivel medio mayor A',
    'Nivel medio mayor B': 'Nivel medio mayor B',
    'Nivel medio mayor C': 'Nivel medio mayor C',
    'Nivel medio mayor D': 'Nivel medio mayor D',
    'Medio Mayor A': 'Nivel medio mayor A',
    'Medio Mayor B': 'Nivel medio mayor B',
    'Medio Mayor C': 'Nivel medio mayor C',
    'Medio Mayor D': 'Nivel medio mayor D',
    // Prekínder (actualizado para mapear "Pre Kinder" a "Prekínder")
    'Prekínder A': 'Prekínder A',
    'Prekínder B': 'Prekínder B',
    'Prekínder C': 'Prekínder C',
    'Pre Kinder A': 'Prekínder A',  // ✅ MAPEO CRÍTICO
    'Pre Kinder B': 'Prekínder B',  // ✅ MAPEO CRÍTICO
    'Pre Kinder C': 'Prekínder C',  // ✅ MAPEO CRÍTICO
    // Kínder
    'Kínder A': 'Kínder A',
    'Kínder B': 'Kínder B',
    'Kínder C': 'Kínder C',
    'Kinder A': 'Kínder A',
    'Kinder B': 'Kínder B',
    'Kinder C': 'Kínder C',
    // Primero básico
    'Primero básico A': 'Primero básico A',
    'Primero básico B': 'Primero básico B',
    'Primero básico C': 'Primero básico C',
    '1° básico A': 'Primero básico A',
    '1° básico B': 'Primero básico B',
    '1° básico C': 'Primero básico C',
    // Segundo básico
    'Segundo básico A': 'Segundo básico A',
    'Segundo básico B': 'Segundo básico B',
    'Segundo básico C': 'Segundo básico C',
    '2° básico A': 'Segundo básico A',
    '2° básico B': 'Segundo básico B',
    '2° básico C': 'Segundo básico C',
    // Tercero básico
    'Tercero básico A': 'Tercero básico A',
    'Tercero básico B': 'Tercero básico B',
    'Tercero básico C': 'Tercero básico C',
    '3° básico A': 'Tercero básico A',
    '3° básico B': 'Tercero básico B',
    '3° básico C': 'Tercero básico C',
    // Cuarto básico
    'Cuarto básico A': 'Cuarto básico A',
    'Cuarto básico B': 'Cuarto básico B',
    'Cuarto básico C': 'Cuarto básico C',
    '4° básico A': 'Cuarto básico A',
    '4° básico B': 'Cuarto básico B',
    '4° básico C': 'Cuarto básico C',
    // Quinto básico
    'Quinto básico A': 'Quinto básico A',
    'Quinto básico B': 'Quinto básico B',
    'Quinto básico C': 'Quinto básico C',
    '5° básico A': 'Quinto básico A',
    '5° básico B': 'Quinto básico B',
    '5° básico C': 'Quinto básico C'
  };

  const shortCourse = courseMap[course] || course; // ✅ Usar el curso mapeado o el original
  const found = teachersInfoCurrent.find(t => t.course === shortCourse);
  // Agregar additionalTeachers vacío para mantener consistencia con el tipo de retorno
  return found ? { ...found, additionalTeachers: [] } : undefined;
};

// Función auxiliar para justificar texto dentro de un ancho dado (excepto la última línea)
const justifyText = (text: string, maxWidth: number, pdf: any, isLastLine: boolean = false): string => {
  const words = text.trim().split(/\s+/);
  if (words.length <= 1 || isLastLine) return text.trim();

  const fontSize = pdf.getFontSize();
  const scaleFactor = pdf.internal.scaleFactor;

  // Ancho actual de la línea (con un solo espacio entre palabras)
  const currentWidth =
    pdf.getStringUnitWidth(words.join(' ')) * fontSize / scaleFactor;

  // Ancho de un espacio simple
  const spaceWidth = pdf.getStringUnitWidth(' ') * fontSize / scaleFactor;

  const gaps = words.length - 1;
  const extraSpace = maxWidth - currentWidth;
  
  // VALIDACIÓN CRÍTICA AGREGADA:
  // Si el espacio extra es EXCESIVO (por ejemplo, más del 30% del ancho total),
  // es probable que sea una línea corta que jsPDF no cortó (ej: final de párrafo forzado o error de cálculo).
  // En ese caso, NO justificamos, tratamos como última línea.
  const excessiveSpaceThreshold = maxWidth * 0.3; // 30% del ancho total como margen libre máximo permitido
  
  if (extraSpace > excessiveSpaceThreshold) {
      return text.trim(); // No justificar si hay demasiado espacio libre
  }

  if (extraSpace <= 0 || !isFinite(extraSpace) || !isFinite(spaceWidth) || spaceWidth === 0) {
    return text.trim();
  }

  const totalExtraSpaces = extraSpace / spaceWidth;
  const baseExtraPerGap = Math.floor(totalExtraSpaces / gaps);
  const remainder = Math.max(0, Math.round(totalExtraSpaces - baseExtraPerGap * gaps));

  const baseSpace = ' '.repeat(1 + baseExtraPerGap);
  let result = '';

  words.forEach((word, index) => {
    result += word;
    if (index < gaps) {
      // Distribuir el resto de los espacios extras en los primeros "remainder" huecos
      const extra = index < remainder ? ' ' : '';
      result += baseSpace + extra;
    }
  });

  return result;
};

// Función para obtener dimensiones filtradas según tenant
const getAllDimensions = (tenantKey: string | null) => {
  const baseDimensions = [
    { id: 1, name: "Autoconciencia y voluntad", dimension: autoconcienciaVoluntadDimension },
    { id: 2, name: "Habilidades pensamiento", dimension: habilidadesPensamientoDimension },
    { id: 3, name: "Habilidades comunicacionales en el lenguaje español", dimension: habilidadesComunicacionalesDimension },
    { id: 4, name: "Corporalidad y Movimiento", dimension: corporalidadMovimientoDimension },
    { id: 5, name: "Lenguaje Artístico", dimension: lenguajesArtisticosDimension },
    { id: 6, name: "Motivación y disposición hacia el aprendizaje", dimension: motivacionAprendizajeDimension },
    { id: 7, name: "Encuentro con la Naturaleza y Sociedad", dimension: encuentroNaturalezaDimension },
    { id: 8, name: "Habilidades de relación y conciencia social", dimension: habilidadesSocialesDimension },
    { id: 9, name: "Pensamiento matemático", dimension: pensamientoMatematicosDimension },
    { id: 10, name: "Acercamiento a la lengua inglesa", dimension: lenguaInglesaDimension },
  ];

  // Solo agregar "Amor a Dios y sentido Cristiano" para tenant CPV
  if (tenantKey === 'cpv.cl' || tenantKey === 'cpv_cl') {
    baseDimensions.push({ id: 11, name: "Amor a Dios y sentido Cristiano", dimension: amorDiosDimension });
  }

  return baseDimensions;
};

// Función para determinar el ciclo según el curso (solo para CPV)
const getCicloText = (course: string): string => {
  // Verificar si es Colegio Puerto Varas
  const isCPV = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        return user.tenantKey === 'cpv_cl' || user.email?.includes('@cpv.cl');
      }
      
      const organizationData = localStorage.getItem('organizationData');
      if (organizationData) {
        const org = JSON.parse(organizationData);
        return org.tenantKey === 'cpv_cl';
      }
      
      // Fallback: revisar URL o dominio
      return window.location.hostname.includes('cpv') || 
             window.location.href.includes('cpv');
    } catch (error) {
      console.warn('Error verificando tenantKey:', error);
      return false;
    }
  };
  
  // Si no es CPV, no mostrar texto de ciclo
  if (!isCPV()) {
    return '';
  }
  
  // ✅ LÓGICA ORIGINAL SOLO PARA CPV: Extraer la parte base del curso (sin A/B/C/D)
  const courseBase = course.split(' ').slice(0, -1).join(' ');
  
  const scottCourseBases = [
    'Nivel medio mayor',
    'Medio Mayor',
    'Prekínder', 
    'Pre Kinder',
    'Prekinder',
    'Kínder',
    'Kinder'
  ];
  
  console.log('🔍 getCicloText Debug (CPV only):', { course, courseBase, isScott: scottCourseBases.includes(courseBase) });
  
  return scottCourseBases.includes(courseBase) ? 'SCOTT' : 'AMUNDSEN';
};

const APP_VERSION = '1.0.1'; // Incrementa esto cada vez que hagas cambios importantes

/* 
🎯 GUÍA DE AJUSTES RÁPIDOS PARA EL USUARIO:

📊 TAMAÑO DE BARRAS:
- Línea 478: const barSize = 80 ← CAMBIAR AQUÍ para barra LOGRADO VISIBLE
- Línea 637: barSize={8} ← CAMBIAR AQUÍ para barra ESPERADO VISIBLE (delgada)
- Línea 2025: barSize={40} ← 🚨 CAMBIAR AQUÍ para barra LOGRADO PDF (reducida por problema)
- Línea 2033: barSize={8} ← 🚨 CAMBIAR AQUÍ para barra ESPERADO PDF (delgada)
- Línea 2041: width={3} height={35} ← 🚨 CAMBIAR AQUÍ para scatter PDF (reducido)
- ⚠️ NOTA: PDF tiene tamaños reducidos para evitar sobreposición del eje X

📅 RANGO DE AÑOS (EXACTAMENTE 6 AÑOS SIEMPRE):
- Línea 550: minAge + 5 ← 🚨 CAMBIAR AQUÍ para gráfico VISIBLE (siempre 6 años)
- Línea 1904: minAge + 5 ← 🚨 CAMBIAR AQUÍ para PDF (siempre 6 años)
- ⚠️ LÓGICA: minAge = Math.max(4, age-2) y maxAge = Math.min(12, minAge+5)

🎯 CICLO SCOTT/AMUNDSEN:
- Línea 303-320: getCicloText() ← CAMBIAR AQUÍ para ajustar qué cursos son SCOTT vs AMUNDSEN

📐 ESPACIADO Y TIPOGRAFÍA PDF:
- Línea 1932: right: 80 ← MÁS ESPACIO derecho para eje X
- Línea 1934: bottom: 80 ← MÁS ESPACIO abajo para eje X
- Línea 1944: fontSize: 20 ← TAMAÑO letra eje X
- Línea 994: pageHeight - 20 ← ALTURA TOTAL del gráfico (era -60)

📝 ETIQUETAS EJE Y (DIMENSIONES) PDF:
- Línea 1984: wrapText(payload.value, 300) ← 🚀 MÁS CARACTERES por línea (era 230)
- Línea 1994: fontSize="34" ← 🚀 LETRA SÚPER GRANDE para dimensiones (actualizada por usuario)  
- Línea 2003: lineHeight * 22 ← 🚀 ESPACIADO entre líneas ajustado

🏛️ ESTRUCTURA DE 3 GRANDES ÁREAS (SUGERIDA):
ÁREA 1 - DESARROLLO PERSONAL Y COGNITIVO:
- Autoconciencia y voluntad
- Habilidades pensamiento
- Motivación y disposición hacia el aprendizaje

ÁREA 2 - COMUNICACIÓN Y EXPRESIÓN:
- Habilidades comunicacionales en el lenguaje español
- Lenguaje Artístico  
- Acercamiento a la lengua inglesa

ÁREA 3 - DESARROLLO SOCIAL, FÍSICO Y ESPIRITUAL:
- Corporalidad y Movimiento
- Encuentro con la Naturaleza y Sociedad
- Habilidades de relación y conciencia social
- Pensamiento matemático
- Amor a Dios y sentido Cristiano
*/

const PDF_CHART_ULTRA_HD_STORAGE_KEY = 'pdfChartUltraHd';

type ChartCaptureProfile = {
  containerWidth: number;
  containerHeight: number;
  captureWidth: number;
  captureHeight: number;
  chartHeight: number;
  scale: number;
  waitMs: number;
  outputFormat: 'png' | 'jpeg';
  outputQuality: number;
  pdfImageCompression: 'MEDIUM' | 'NONE';
};

const CHART_LAYOUT = {
  containerWidth: 1400,
  containerHeight: 1050,
  captureWidth: 1400,
  captureHeight: 920,
  chartHeight: 1000,
} as const;

const CHART_CAPTURE_PROFILES: Record<'standard' | 'highQuality', ChartCaptureProfile> = {
  standard: {
    ...CHART_LAYOUT,
    scale: 1.0,
    waitMs: 3000,
    outputFormat: 'png',
    outputQuality: 1.0,
    pdfImageCompression: 'MEDIUM',
  },
  highQuality: {
    ...CHART_LAYOUT,
    scale: 2.0,
    waitMs: 3500,
    outputFormat: 'jpeg',
    outputQuality: 0.92,
    pdfImageCompression: 'MEDIUM',
  },
};

const getChartCaptureProfile = (highQuality: boolean) =>
  CHART_CAPTURE_PROFILES[highQuality ? 'highQuality' : 'standard'];

const VisualizacionResultados: React.FC<Props> = ({ 
  students, 
  exportToExcel, 
  exportToPDF, 
  exportPDFMassive,
  onSectionChange,
  onDimensionSelect,
  onViewModeChange
}) => {
  // Obtener contexto de tenant
  const { tenantKey } = useTenant();
  
  // Obtener dimensiones filtradas según tenant
  const allDimensions = useMemo(() => getAllDimensions(tenantKey), [tenantKey]);
  
  // Filtrar categorías según tenant
  const filteredDimensionCategories = useMemo(() => {
    const baseCategories = {
      "Exploración de uno mismo": [
        "Autoconciencia y voluntad",
        "Habilidades pensamiento",
        "Habilidades comunicacionales en el lenguaje español",
        "Corporalidad y Movimiento",
        "Lenguaje Artístico",
        "Motivación y disposición hacia el aprendizaje"
      ],
      "Exploración del mundo": [
        "Encuentro con la Naturaleza y Sociedad",
        "Habilidades de relación y conciencia social",
        "Pensamiento matemático",
        "Acercamiento a la lengua inglesa"
      ]
    };

    // Solo agregar categoría trascendente para tenant CPV
    if (tenantKey === 'cpv.cl' || tenantKey === 'cpv_cl') {
      return {
        ...baseCategories,
        "Exploración de lo trascendente": [
          "Amor a Dios y sentido Cristiano"
        ]
      };
    }

    return baseCategories;
  }, [tenantKey]);
  
  // Eliminado viewMode - siempre será por estudiante
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<(number | string)[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>(students);
  const chartRef = useRef<HTMLDivElement>(null);
  const [currentTeachers, setCurrentTeachers] = useState<{spanish: string, english: string, additional?: Array<{ name: string; role: string }>} | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedStudentForPreview, setSelectedStudentForPreview] = useState<Student | null>(null);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [showMassPdfMenu, setShowMassPdfMenu] = useState(false);
  const [showIndividualPdfMenu, setShowIndividualPdfMenu] = useState(false);
  const massPdfMenuRef = useRef<HTMLDivElement>(null);
  const individualPdfMenuRef = useRef<HTMLDivElement>(null);
  const [useUltraHdCharts, setUseUltraHdCharts] = useState(() => {
    try {
      const stored = localStorage.getItem(PDF_CHART_ULTRA_HD_STORAGE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  
  // Tutorial manejado por TutorialSection

  // Estados para la exportación masiva por cursos
  const [selectedCoursesForExport, setSelectedCoursesForExport] = useState<string[]>([]);
  const [showCourseSelectionModal, setShowCourseSelectionModal] = useState(false);
  const [massExportIncludeQualitative, setMassExportIncludeQualitative] = useState(true);
  const [isExportingMassive, setIsExportingMassive] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  
  // Estados mejorados para exportación masiva
  const [exportState, setExportState] = useState<{
    isRunning: boolean;
    isPaused: boolean;
    currentIndex: number;
    totalCount: number;
    processedCount: number;
    errorCount: number;
    errors: string[];
    startTime: number | null;
    estimatedTimeRemaining: number;
  }>({
    isRunning: false,
    isPaused: false,
    currentIndex: 0,
    totalCount: 0,
    processedCount: 0,
    errorCount: 0,
    errors: [],
    startTime: null,
    estimatedTimeRemaining: 0
  });
  
  // Estado para manejo de sesión durante exportación
  const [sessionStatus, setSessionStatus] = useState<{
    lastRefresh: number;
    refreshCount: number;
    isRefreshing: boolean;
  }>({
    lastRefresh: Date.now(),
    refreshCount: 0,
    isRefreshing: false
  });

  // Ref para acceder al estado actual de exportación dentro de closures
  const exportStateRef = useRef(exportState);

  // Actualizar el ref cada vez que cambie el estado
  useEffect(() => {
    exportStateRef.current = exportState;
  }, [exportState]);

  // Tutorial ahora manejado por TutorialSection - lógica removida

  const courses = useMemo(() => {
    // ✅ ORDENAMIENTO MEJORADO - Función que extrae el orden base del curso
    // Usar la función centralizada para obtener el orden de cursos

    return Array.from(new Set(students.map(s => s.course)))
      .sort((a, b) => {
        const orderA = getCourseDetailedOrder(a);
        const orderB = getCourseDetailedOrder(b);
        
        if (orderA === orderB) {
          // Si tienen el mismo orden base, ordenar por letra (A, B, C)
          return a.localeCompare(b);
        }
        
        return orderA - orderB;
      });
  }, [students]);

  useEffect(() => {
    if (students.length > 0 && selectedStudentIds.length === 0) {
      setSelectedStudentIds([students[0].id]);
    }
  }, [students]);

  useEffect(() => {
    if (courses.length > 0 && (!selectedCourse || !courses.includes(selectedCourse))) {
      setSelectedCourse(courses[0]);
    }
  }, [courses, selectedCourse]);

  useEffect(() => {
    const filtered = selectedCourse
      ? students.filter(student => student.course === selectedCourse)
      : students;
    setFilteredStudents(filtered);
    updateChartData();
  }, [selectedCourse, students]);

  // Función simplificada - siempre es por estudiante
  const updateChartData = useCallback(() => {
    let data: any[] = [];
    if (selectedStudentIds.length > 0) {
      selectedStudentIds.forEach(studentId => {
        const student = findStudentById(filteredStudents, studentId);
        if (student) {
          // Crear objeto StudentData compatible para usar las funciones de cálculo
          const studentData: any = {
            id: student.id,
            name: student.name,
            course: student.course,
            birthDate: student.birthDate,
            skillLevels: student.skillLevels
          };

          allDimensions.forEach(({ id, name, dimension }) => {
            // 🎯 NUEVO SISTEMA: Usar cálculo por tramos de edad
            const tramoResult = calculateProgressByAgeTramos(id, name, student.course, student.skillLevels);

            // Usar el porcentaje calculado por tramos
            const percentageAchieved = tramoResult.percentage;

            data.push({
              name: name,
              studentName: student.name,
              percentageAchieved: Math.round(percentageAchieved),
              // Unificar por curso/edad ancla A: esperado=A+1 (cap en 12)
              expectedLevel: calculateExpectedPercentage(deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true }))
            });
          });
        }
      });
    }
    setChartData(data);
  }, [selectedStudentIds, filteredStudents]);

  // Esta función se mantuvo por compatibilidad pero usaremos generatePDFBlobForZip para ZIP

  // Función mejorada para exportación masiva con carpetas por curso
  const openMassExportModal = (includeQualitative: boolean) => {
    setMassExportIncludeQualitative(includeQualitative);
    setShowMassPdfMenu(false);
    setShowCourseSelectionModal(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (massPdfMenuRef.current && !massPdfMenuRef.current.contains(event.target as Node)) {
        setShowMassPdfMenu(false);
      }
      if (individualPdfMenuRef.current && !individualPdfMenuRef.current.contains(event.target as Node)) {
        setShowIndividualPdfMenu(false);
      }
    };

    if (showMassPdfMenu || showIndividualPdfMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMassPdfMenu, showIndividualPdfMenu]);

  useEffect(() => {
    try {
      localStorage.setItem(PDF_CHART_ULTRA_HD_STORAGE_KEY, String(useUltraHdCharts));
    } catch {
      // Ignorar si localStorage no está disponible
    }
  }, [useUltraHdCharts]);

  const exportPDFMassiveByCourseFolders = async (includeQualitativePage: boolean) => {
    if (selectedCoursesForExport.length === 0) {
      alert('Por favor selecciona al menos un curso para exportar.');
      return;
    }

    const studentsToExport = students.filter(student => 
      selectedCoursesForExport.includes(student.course)
    );

    const BATCH_SIZE = 2; // Lotes MUY pequeños para evitar timeout en producción

    // Inicializar estados
    setIsExportingMassive(true);
    setExportProgress(0);
    setExportState({
      isRunning: true,
      isPaused: false,
      currentIndex: 0,
      totalCount: studentsToExport.length,
      processedCount: 0,
      errorCount: 0,
      errors: [],
      startTime: Date.now(),
      estimatedTimeRemaining: 0
    });

    console.log(`🚀 Iniciando exportación masiva ZIP mejorada para ${studentsToExport.length} estudiantes en ${selectedCoursesForExport.length} cursos`);

    try {
      // Renovar token inicial
      await refreshTokenIfNeeded(true);

      const zip = new JSZip();
      const totalStudents = studentsToExport.length;
      let processedStudents = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Agrupar estudiantes por curso
      const studentsByCourse = studentsToExport.reduce((acc, student) => {
        if (!acc[student.course]) {
          acc[student.course] = [];
        }
        acc[student.course].push(student);
        return acc;
      }, {} as Record<string, Student[]>);

      // Crear carpetas por curso
      const courseFolders: Record<string, JSZip | null> = {};
      Object.keys(studentsByCourse).forEach(course => {
        const courseFolderName = course.replace(/[<>:"/\\|?*]/g, '_');
        courseFolders[course] = zip.folder(courseFolderName);
      });

      // Función para procesar un lote de estudiantes con renewal de token
      const processBatchForZip = async (students: Student[], startIndex: number, batchSize: number): Promise<{ success: number; errors: string[] }> => {
        const results = { success: 0, errors: [] as string[] };
        const endIndex = Math.min(startIndex + batchSize, students.length);
        
        for (let i = startIndex; i < endIndex; i++) {
          // Verificar si está pausado
          if (exportStateRef.current.isPaused) {
            console.log('⏸️ Exportación pausada');
            break;
          }

          if (!exportStateRef.current.isRunning) {
            console.log('🛑 Exportación detenida');
            break;
          }

          const student = students[i];
          
          try {
            console.log(`📄 Procesando ZIP ${i + 1}/${students.length}: ${student.name} (${student.course})`);
            
            // Actualizar estado
            setExportState(prev => ({
              ...prev,
              currentIndex: i + 1,
              processedCount: prev.processedCount + 1
            }));
            
            // Generar PDF como blob
            const pdfBlob = await generatePDFBlobForZip(student, includeQualitativePage, useUltraHdCharts);
            const rutLimpio = extractRutFromStudent(student);
            const fileName = `${rutLimpio}-informe_integral.pdf`;
            
            // Agregar al ZIP en la carpeta correspondiente
            const courseFolder = courseFolders[student.course];
            courseFolder?.file(fileName, pdfBlob);
            
            results.success++;
            
            // Pausa MAYOR entre estudiantes para liberar memoria (producción)
            await new Promise(resolve => setTimeout(resolve, 500));
            
          } catch (error) {
            const errorMsg = `Error procesando ${student.name}: ${error instanceof Error ? error.message : 'Error desconocido'}`;
            console.error(`❌ ${errorMsg}`);
            results.errors.push(errorMsg);
            
            setExportState(prev => ({
              ...prev,
              errorCount: prev.errorCount + 1,
              errors: [...prev.errors, errorMsg]
            }));
          }
          
          // Actualizar progreso
          const progressPercentage = ((i + 1) / students.length) * 90; // 90% para procesamiento, 10% para ZIP
          setExportProgress(progressPercentage);
          
          // Actualizar estimación de tiempo
          const elapsed = Date.now() - (exportStateRef.current.startTime || Date.now());
          const rate = (i + 1) / elapsed;
          const remaining = (students.length - (i + 1)) / rate;
          
          setExportState(prev => ({
            ...prev,
            estimatedTimeRemaining: remaining
          }));
        }
        
        return results;
      };

      let currentIndex = 0;

      // Función recursiva para procesar lotes en background
      const processNextBatchForZip = async (): Promise<void> => {
        return new Promise((resolve) => {
          // Usar requestAnimationFrame para no bloquear la UI
          requestAnimationFrame(async () => {
            try {
              // Verificar si está pausado o detenido
              if (exportStateRef.current.isPaused) {
                console.log('⏸️ Exportación ZIP pausada, esperando...');
                setTimeout(() => processNextBatchForZip().then(resolve), 2000);
                return;
              }
              
              if (!exportStateRef.current.isRunning) {
                console.log('🛑 Exportación ZIP detenida');
                resolve();
                return;
              }
              
              // Renovar token cada 8 estudiantes (menos frecuente para ZIP)
              if (currentIndex > 0 && currentIndex % 8 === 0) {
                const tokenRefreshed = await refreshTokenIfNeeded();
                if (!tokenRefreshed) {
                  console.error('❌ No se pudo renovar el token, deteniendo exportación ZIP');
                  setExportState(prev => ({ ...prev, isRunning: false }));
                  resolve();
                  return;
                }
              }
              
              // Procesar lote
              const batchResults = await processBatchForZip(studentsToExport, currentIndex, BATCH_SIZE);
              processedStudents += batchResults.success;
              errorCount += batchResults.errors.length;
              errors.push(...batchResults.errors);
              
              currentIndex += BATCH_SIZE;
              
              // Verificar si quedan más estudiantes
              if (currentIndex < studentsToExport.length && exportStateRef.current.isRunning) {
                // Continuar con el siguiente lote
                processNextBatchForZip().then(resolve);
              } else {
                // Completado el procesamiento
                resolve();
              }
            } catch (error) {
              console.error('❌ Error en procesamiento de lotes ZIP:', error);
              resolve();
            }
          });
        });
      };

      // Procesar todos los estudiantes
      await processNextBatchForZip();

      // Generar el ZIP (10% restante del progreso)
      setExportProgress(90);
      console.log('📦 Generando archivo ZIP...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Descargar el archivo
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `Informes_PDF_por_Cursos_${dateStr}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      setExportProgress(100);

      const finalMessage = `✅ Exportación ZIP completada!\n\n` +
        `📊 Resumen:\n` +
        `• Total procesados: ${processedStudents}/${studentsToExport.length}\n` +
        `• Cursos: ${selectedCoursesForExport.length}\n` +
        `• Errores: ${errorCount}\n` +
        `• Token renovado: ${sessionStatus.refreshCount} veces\n` +
        `• Tiempo total: ${Math.round((Date.now() - (exportState.startTime || 0)) / 1000)} segundos`;
      
      console.log(finalMessage);
      alert(finalMessage);

      if (errors.length > 0) {
        console.log('❌ Errores encontrados en ZIP:', errors);
      }

    } catch (error) {
      console.error('Error durante la exportación masiva ZIP:', error);
      alert('Hubo un error durante la exportación ZIP. Revisa la consola para más detalles.');
    } finally {
      setIsExportingMassive(false);
      setExportProgress(0);
      setExportState(prev => ({ ...prev, isRunning: false, isPaused: false }));
      setShowCourseSelectionModal(false);
    }
  };

  // Función para generar PDF completo como blob para ZIP (copia exacta de generatePDF)
  const generatePDFBlobForZip = async (student: Student, includeQualitativePage = true, ultraHd = true): Promise<Blob> => {
    try {
      const profile = getChartCaptureProfile(ultraHd);
      const chartImage = await captureNewChart(student, ultraHd);
      
      // Usar exactamente la misma lógica que generatePDF pero retornar blob
      const pdf = new jsPDF({
        compress: true,
        unit: 'mm',
        format: [215.9, 279.4], // Formato Carta (Letter) explícito
        putOnlyUsedFonts: true, // Solo incluir fuentes usadas
        orientation: 'portrait',
        floatPrecision: 2 // Reducir precisión decimal para menor tamaño
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const BG_COLOR = '#FFFFFF';
      const cornerRadius = 5;

      // Agregar versión en alguna esquina del PDF
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      pdf.text(`v${APP_VERSION}`, pdf.internal.pageSize.getWidth() - 20, pdf.internal.pageSize.getHeight() - 10);
      
      // Función para añadir el fondo blanco y la barra lateral
      const addBackgroundAndSidebar = (isCoverPage = false) => {
        // Agregar fondo blanco
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

        // Obtener colores de branding personalizados
        const brandingColors = getTenantBrandingColors();
        const primaryColor = brandingColors.primary;

        // Ajustar ancho y posición según si es portada o no
        const leftMargin = isCoverPage ? 10 : 0; // Más espacio en blanco a la izquierda en portada
        const blueBarWidth = isCoverPage ? 55 : 20; // Aumentado a 55 para portada (de 40 a 55), normal para otras páginas
        pdf.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b); // Color primario personalizado
        pdf.rect(leftMargin, 0, blueBarWidth, pageHeight, 'F');

        // Texto "Ciclo Amundsen" o "Ciclo Scott" vertical
        const cicloText = getCicloText(student.course);
        
        // Solo renderizar si hay texto de ciclo (solo para CPV)
        if (cicloText.length > 0) {
          // Convertir a formato con mayúscula inicial solamente
          const formattedCicloText = cicloText.charAt(0).toUpperCase() + cicloText.slice(1).toLowerCase();
          const fullText = `Ciclo ${formattedCicloText}`;
          
          pdf.setFontSize(isCoverPage ? 42 : 28); // Triple para portada (14*3=42), grande para internas
          pdf.setTextColor(180, 180, 180); // Gris claro
          pdf.setFont('helvetica', 'normal');
          
          if (isCoverPage) {
            // Para portada: texto vertical a la derecha de la barra, con margen desde el logo
            const logoWidth = 32;
            const logoHeight = logoWidth * 1.2;
            const logoY = 25; // Movido más abajo (de 9 a 25)
            
            // Ajustar margen según si es Amundsen (más largo) o Scott
            const isAmundsen = formattedCicloText.toLowerCase() === 'amundsen';
            const extraMargin = isAmundsen ? 30 : 0; // 30mm extra para Amundsen
            const logoBottomY = logoY + logoHeight + 95 + extraMargin; // Margen ajustado según ciclo
            
            const textX = leftMargin + blueBarWidth + 20; // 20mm de margen desde la barra
            const textY = logoBottomY; // Inicia desde el margen inferior del logo
            
            pdf.saveGraphicsState();
            pdf.text(fullText, textX, textY, {
              angle: 90 // Rotación vertical
            });
            pdf.restoreGraphicsState();
          } else {
            // Para páginas internas: texto vertical dentro de la barra
            const xPosition = blueBarWidth / 2 - 1;
            const bottomMargin = 30;
            const yPosition = pageHeight - bottomMargin;
            
            pdf.saveGraphicsState();
            pdf.text(fullText, xPosition, yPosition, {
              angle: 90
            });
            pdf.restoreGraphicsState();
          }
        }
      };

      // Función para añadir el header a cada página
      const addHeader = async () => {
        const blueBarWidth = 20;
        const logoSizes = getTenantLogoSize();
        
        // Logo centrado en el borde de la barra lateral (mitad dentro, mitad fuera)
        try {
          const tenantLogo = getTenantLogo();
          const logoBase64 = await loadImageAsBase64(tenantLogo);
          const logoWidth = logoSizes.header.width; // Tamaño configurable
          const logoHeight = logoSizes.header.height;
          const logoX = blueBarWidth - (logoWidth / 2); // Centrado en el borde de la barra
          const logoY = 6; // Ajustado para el logo más grande
          pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'FAST');
        } catch (error) {
          console.error('Error al agregar el logo al header:', error);
        }
        
        // Línea vertical decorativa más a la derecha
        const lineX = 35;
        pdf.setDrawColor(0, 0, 0); // Negro oscuro
        pdf.setLineWidth(0.5);
        pdf.line(lineX, 12, lineX, 28);
        
        // Nombre y Curso en formato horizontal
        const textX = lineX + 5;
        
        // Nombre (label y valor en la misma línea)
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Negro oscuro
        pdf.text('Nombre alumno/a', textX, 18);
        
        const labelWidth = pdf.getStringUnitWidth('Nombre alumno/a') * 9 / pdf.internal.scaleFactor;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text(student.name, textX + labelWidth + 3, 18);
        
        // Curso (label y valor en la misma línea)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0); // Negro oscuro
        pdf.text('Curso', textX, 25);
        
        const cursoLabelWidth = pdf.getStringUnitWidth('Curso') * 9 / pdf.internal.scaleFactor;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text(student.course, textX + cursoLabelWidth + 3, 25);
      };

      // Portada
      addBackgroundAndSidebar(true); // true indica que es la portada
      
      // Logo en portada - sobre la barra lateral
      try {
        const tenantLogo = getTenantLogo();
        const logoBase64 = await loadImageAsBase64(tenantLogo);
        const logoSizes = getTenantLogoSize();
        const leftMargin = 10; // Mismo margen que la barra
        const blueBarWidth = 55; // Mismo ancho que la barra de portada (actualizado a 55)
        const logoWidth = logoSizes.cover.width; // Tamaño configurable
        const logoHeight = logoSizes.cover.height;
        const logoX = leftMargin + blueBarWidth - (logoWidth / 2); // Centrado en el borde de la barra
        const logoY = 20; // Ajustado para el logo más grande
        const logoBottomY = logoY + logoHeight; // Posición inferior del logo
        pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'FAST');
      } catch (error) {
        console.error('Error al agregar el logo a la portada:', error);
      }
      
      // Línea vertical de énfasis en el cuadrante derecho inferior
      const verticalLineX = pageWidth / 2 + 10; // Más a la derecha del centro
      
      // Contenido en el cuadrante derecho inferior
      const contentStartX = verticalLineX + 15; // Margen desde la línea vertical
      const contentStartY = pageHeight / 2 + 30; // Posición de inicio del título (ajustado)
      const lineSpacing = 15; // Aumentado de 13 a 15 para más espacio
      let currentY = contentStartY;
      
      // Línea vertical inicia a la misma altura que el título y termina en el borde inferior
      const verticalLineYStart = contentStartY - 5; // Inicia a la altura del título
      const verticalLineYEnd = pageHeight; // Termina en el borde inferior sin margen
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(1);
      pdf.line(verticalLineX, verticalLineYStart, verticalLineX, verticalLineYEnd);
      
      // Título: cada palabra en una línea
      pdf.setFontSize(32); // Aumentado de 28 a 32
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      
      pdf.text('Informe', contentStartX, currentY);
      currentY += lineSpacing;
      
      pdf.text('Pedagógico', contentStartX, currentY);
      currentY += lineSpacing;
      
      pdf.text('Integral', contentStartX, currentY);
      currentY += lineSpacing + 8; // Espacio extra antes de la información del alumno
      
      // Información del alumno - más grande
      pdf.setFontSize(11); // Aumentado de 9 a 11
      pdf.setFont('helvetica', 'normal');
      
      pdf.text(`Nombre alumno/a`, contentStartX, currentY);
      currentY += 5;
      pdf.text(student.name, contentStartX, currentY);
      currentY += 9; // Aumentado de 7 a 9
      
      pdf.text('Curso', contentStartX, currentY);
      currentY += 5;
      pdf.text(student.course, contentStartX, currentY);
      currentY += 9; // Aumentado de 7 a 9

      pdf.text('Año', contentStartX, currentY);
      currentY += 5;
      pdf.text(String(new Date().getFullYear()), contentStartX, currentY);
      currentY += 9;
      
      const teacherInfo = await getTeacherInfoForVisualization(student.course);
      const spanish = teacherInfo?.spanishTeacher?.trim();
      const english = teacherInfo?.englishTeacher?.trim();
      const additionalTeachers = teacherInfo?.additionalTeachers || [];
      
      // Crear lista de todos los profesores únicos
      const allTeachers: string[] = [];
      if (spanish) allTeachers.push(spanish);
      if (english && english !== spanish) allTeachers.push(english);
      // Agregar profesores adicionales
      additionalTeachers.forEach((teacher: { name: string; role: string }) => {
        if (teacher.name && !allTeachers.includes(teacher.name)) {
          allTeachers.push(teacher.name);
        }
      });

      const teachersLabel = allTeachers.length > 1 ? 'Docentes' : 'Docente';
      pdf.text(teachersLabel, contentStartX, currentY);
      currentY += 5;

      const teachersText = allTeachers.length > 0
        ? allTeachers.join(' - ')
        : 'No hay información disponible';

      // Dividir el texto si es muy largo para que no se salga del margen
      const maxWidth = pageWidth - contentStartX - 10;
      const teacherLines = pdf.splitTextToSize(teachersText, maxWidth);
      teacherLines.forEach((line: string, index: number) => {
        pdf.text(line, contentStartX, currentY + (index * 5)); // Aumentado de 4 a 5
      });

      // Página del gráfico con estilo original del colegio
      pdf.addPage();
      addBackgroundAndSidebar();
      await addHeader();

      if (chartImage) {
        // SIN TÍTULO - usar todo el espacio disponible
        
        // Configuración MÁXIMA para usar todo el alto disponible
        const chartAreaTop = 40; // Mínimo espacio desde arriba (sin título)
        const chartAreaBottom = pageHeight - 20; // Mínimo espacio abajo
        const maxChartHeight = chartAreaBottom - chartAreaTop;
         
        // Márgenes optimizados para usar TODO el espacio
        const sidebarWidth = 20; // Ancho de la barra lateral
        const leftMargin = sidebarWidth + 10; // Mínimo después de la barra
        const rightMargin = 10; // Margen derecho mínimo
        const maxChartWidth = pageWidth - leftMargin - rightMargin;
         
        // Usar MÁXIMO espacio disponible (casi el 100%)
        const actualChartWidth = maxChartWidth * 0.98; // 98% del ancho
        const actualChartHeight = maxChartHeight * 0.95; // 95% de la altura total
        
        // Posicionar el gráfico centrado horizontalmente
        const chartX = leftMargin + (maxChartWidth - actualChartWidth) / 2;
        const chartY = chartAreaTop;
        
        // Agregar imagen del gráfico con tamaño MÁXIMO
        const imageFormat = chartImage.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
        const compressionLevel = profile.pdfImageCompression;
        
        pdf.addImage(chartImage, imageFormat, chartX, chartY, actualChartWidth, actualChartHeight, '', compressionLevel);
      } else {
        // Mensaje centrado si no hay gráfico
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(150, 150, 150);
        pdf.text('No se pudo cargar el gráfico', pageWidth / 2, pageHeight / 2, { align: 'center' });
        pdf.setTextColor(0, 0, 0);
      }

      if (includeQualitativePage) {
        // Página 3: Invitación al trabajo colaborativo (Análisis Cualitativa)
        pdf.addPage();
        addBackgroundAndSidebar();
        await addHeader();

        const titleY = 50;
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Invitación al trabajo colaborativo', 30, titleY);
        
        // Configuración del texto
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const description = getQualitativeDescription(student);
        const textLeftMargin = 30;
        const textRightMargin = 30; // Aumentado de 10 a 30 para más margen
        const textPadding = 10;
        const containerWidth = pageWidth - (textLeftMargin + textRightMargin);
        const textAreaWidth = containerWidth - (2 * textPadding); // Ancho real disponible para texto
        // Pre-procesar el texto para dividir por párrafos y luego por ancho
        const rawParagraphs = description.split(/\r?\n/);
        const splitText: { text: string; isLast: boolean }[] = [];

        rawParagraphs.forEach((paragraph) => {
          // Ignorar párrafos vacíos si se desea, o manejarlos como espacio vertical
          if (!paragraph.trim()) {
             return;
          }
          const lines = pdf.splitTextToSize(paragraph, textAreaWidth);
          lines.forEach((line: string, idx: number) => {
            splitText.push({
              text: line,
              isLast: idx === lines.length - 1 // Es última línea si es la última del wrap de este párrafo
            });
          });
        });

        // Altura de línea más amplia para evitar solapes en PDF
        const lineHeight = 5.5;
        let qualitativeY = titleY + 20;
        
        // Calcular altura total del texto en la página actual
        const calculateTextHeight = (lines: { text: string; isLast: boolean }[], startIndex: number, maxY: number): number => {
          let height = textPadding * 2;
          let currentLine = startIndex;
          while (currentLine < lines.length) {
            const nextHeight = height + lineHeight;
            if (qualitativeY + nextHeight > maxY) break;
            height = nextHeight;
            currentLine++;
          }
          return height;
        };

        // Dibujar el contenido de la descripción cualitativa
        let currentLineIndex = 0;
        while (currentLineIndex < splitText.length) {
          const pageMaxY = pageHeight - 20;
          const availableHeight = pageMaxY - qualitativeY - textPadding * 2;
          const maxLinesInPage = Math.floor(availableHeight / lineHeight);
          const endLineIndex = Math.min(currentLineIndex + maxLinesInPage, splitText.length);
          
          const linesInCurrentPage = splitText.slice(currentLineIndex, endLineIndex);
          
          if (linesInCurrentPage.length > 0) {
            const boxHeight = linesInCurrentPage.length * lineHeight + textPadding * 2;
            
            // Fondo suave para el texto
            pdf.setFillColor(250, 250, 250);
            pdf.roundedRect(textLeftMargin, qualitativeY, containerWidth, boxHeight, cornerRadius, cornerRadius, 'F');
            
            // Dibujar el texto línea por línea con mejor espaciado
            pdf.setTextColor(60, 60, 60);
            pdf.setFont('helvetica', 'normal');
            
            linesInCurrentPage.forEach((lineObj, index) => {
              // Justificar usando la marca de 'isLast' precalculada
              const justifiedLine = justifyText(lineObj.text.trim(), textAreaWidth, pdf, lineObj.isLast);
              const yPosition = qualitativeY + textPadding + 5 + (index * lineHeight);
              // Evitar que jsPDF vuelva a envolver (ya usamos splitTextToSize)
              pdf.text(justifiedLine, textLeftMargin + textPadding, yPosition, { align: 'left' });
            });
          }
          
          currentLineIndex = endLineIndex;
          
          // Si hay más líneas, crear una nueva página
          if (currentLineIndex < splitText.length) {
            pdf.addPage();
            addBackgroundAndSidebar();
            await addHeader();
            qualitativeY = margin + 15;
          }
        }
      }

      const descriptionBlocks = getVisibleSubdimensionDescriptionsForPdf(
        student,
        tenantKey
      );
      if (descriptionBlocks.length > 0) {
        await renderMethodologicalNoteTable(pdf, {
          sectionTitle: 'Descripción de Subdimensiones',
          blocks: descriptionBlocks.map((block) => ({
            title: block.displayTitle,
            paragraphs: block.paragraphs,
          })),
          layout: {
            pageWidth,
            pageHeight,
            margin,
            textLeftMargin: 32,
            textRightMargin: 10,
          },
          setupPage: async () => {
            addBackgroundAndSidebar();
            await addHeader();
          },
        });
      }

      // Retornar como blob en lugar de guardar
      return pdf.output('blob');
      
    } catch (error) {
      console.error(`Error generando PDF completo para ${student.name}:`, error);
      // Retornar PDF básico en caso de error
      const pdf = new jsPDF();
      pdf.text(`Error generando informe para ${student.name}`, 20, 20);
      return pdf.output('blob');
    }
  };

  // Función para manejar selección de cursos
  const handleCourseToggleForExport = (course: string) => {
    setSelectedCoursesForExport(prev => 
      prev.includes(course) 
        ? prev.filter(c => c !== course)
        : [...prev, course]
    );
  };

  // Función para seleccionar/deseleccionar todos los cursos
  const handleSelectAllCoursesForExport = () => {
    if (selectedCoursesForExport.length === courses.length) {
      setSelectedCoursesForExport([]);
    } else {
      setSelectedCoursesForExport([...courses]);
    }
  };

  useEffect(() => {
    console.log("Datos de gráfico actualizados para estudiante(s):", selectedStudentIds);
    updateChartData();
  }, [selectedCourse, selectedStudentIds, students, updateChartData]);

  const handleStudentSelect = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>, id: number | string) => {
    if (!id) return;
    if (event.ctrlKey || event.metaKey) {
      setSelectedStudentIds(prev => 
        prev.some(sid => String(sid) === String(id)) ? prev.filter(studentId => String(studentId) !== String(id)) : [...prev, id]
      );
    } else {
      setSelectedStudentIds([id]);
    }
  };

  // Función simplificada - ya no necesitamos cambio de modo
  const initializeSelection = () => {
    setSelectedStudentIds(students.length > 0 && students[0].id ? [students[0].id] : []);
    setSelectedCourse(courses[0]);
  };

  const handleCourseSelect = (value: string) => {
    console.log("Seleccionando curso:", value);
    setSelectedCourse(value);
  };

  const renderChart = (categoryData: any[]) => {
    if (categoryData.length === 0) {
      return <p>No hay datos disponibles para mostrar.</p>;
    }

    // 📊 CONFIGURACIÓN DE BARRAS - AJUSTAR AQUÍ SI NECESITAS CAMBIAR TAMAÑOS
    const yAxisWidth = 400; // MÁS ANCHO para etiquetas con máximo espacio izquierdo
    const textWidth = 230; // Más caracteres por línea para aprovechar el espacio
    const barSize = 80; // 🎯 TAMAÑO BARRAS GRÁFICO VISIBLE - Logrado y Esperado IGUALES
    
    // Altura MAXIMIZADA para utilizar TODO el espacio disponible 
    const heightPerItem = Math.max(80, 600 / categoryData.length); // Altura MÁS ALTA adaptable
    const minChartHeight = categoryData.length * heightPerItem; 
    const chartHeight = minChartHeight;

    // ✅ RANGO DINÁMICO: usar un solo estudiante ancla y curso/edad para A
    let minAge = BASE_MIN_AGE, maxAge = BASE_MAX_AGE;
    let ageTicks: number[] = [];
    let ageMap: { [key: number]: string } = {};
    let offsetValue = 0;

    if (selectedStudentIds.length > 0) {
      const primaryStudentId = selectedStudentIds[0];
      const student = findStudentById(filteredStudents, primaryStudentId);
      if (student) {
        const anchoredAge = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
        const range = getSixYearRangeForAge(anchoredAge);
        minAge = range.minAge;
        maxAge = range.maxAge;
        const ticksAndLabels = createAgeTicksAndLabels(minAge, maxAge);
        ageTicks = ticksAndLabels.ticks;
        // Etiquetas estilo visible
        ageMap = Object.fromEntries(Object.entries(ticksAndLabels.labels).map(([k, v]) => [Number(k), `Hacia los ${v}`]));
        offsetValue = chartOffsetForMinAge(minAge);
        console.log('🔍 Rango Dinámico Unificado:', { studentName: student.name, anchoredAge, minAge, maxAge });
      }
    }
    
    // Transformar los datos del gráfico para que sean relativos al nuevo origen
    const transformedCategoryData = categoryData.map(item => ({
      ...item,
      percentageAchieved: Math.max(0, item.percentageAchieved - offsetValue),
      expectedLevel: Math.max(0, item.expectedLevel - offsetValue),
      previousYearLevel: Math.max(0, item.previousYearLevel - offsetValue)
    }));

    return (
      <div className="w-full bg-white" style={{ height: `${chartHeight}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            layout="vertical"
            data={transformedCategoryData}
            margin={{ 
              top: 15,
              right: 15, // Mínimo necesario
              left: -150, // NEGATIVO para maximizar espacio izquierdo
              bottom: 45 // Más espacio para etiquetas del eje X
            }}
            style={{ backgroundColor: 'white' }}
          >
            <XAxis 
              type="number" 
              domain={[0, (maxAge - minAge) * 20 + 20]}
              ticks={ageTicks}
              tickFormatter={(value) => ageMap[value] || ''}
              tick={({ x, y, payload }) => (
                <text
                  x={x}
                  y={y + 15}
                  textAnchor="middle"
                  fill="#1A202C" // ✅ MÁS PROFESIONAL (más oscuro)
                  fontSize="15" // ✅ MÁS PROFESIONAL (+2px)
                  fontFamily="Inter, Arial, sans-serif" // ✅ FUENTE PROFESIONAL
                  fontWeight="700" // ✅ MÁS PROFESIONAL (más bold)
                >
                  {ageMap[payload.value] || ''}
                </text>
              )}
              axisLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }} // ✅ LÍNEA MÁS PROFESIONAL
              tickLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }}
              interval={0}
              padding={{ left: 0, right: 0 }}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={yAxisWidth}
              tick={({ x, y, payload }) => {
                const lines = wrapText(payload.value, textWidth);
                // Limitar a máximo 2 líneas
                const finalLines = lines.slice(0, 2);
                const lineHeight = 18; // ✅ CONSISTENTE con PDF (más espaciado)
                const totalHeight = finalLines.length * lineHeight;
                const startY = -(totalHeight / 2) + (lineHeight / 2);

                return (
                  <g transform={`translate(${x},${y})`}>
                    {finalLines.map((line: string, index: number) => (
                      <text
                        key={index}
                        x={-8} // MENOS negativo para usar MÁS espacio hacia la izquierda
                        y={startY + (index * lineHeight)}
                        textAnchor="end"
                        fill="#1A202C" // ✅ MÁS PROFESIONAL (más oscuro)
                        fontSize="16" // ✅ MÁS PROFESIONAL (+2px)
                        fontFamily="Inter, Arial, sans-serif" // ✅ FUENTE PROFESIONAL
                        fontWeight="700" // ✅ MÁS PROFESIONAL (más bold)
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                );
              }}
              axisLine={{ stroke: '#CBD5E0', strokeWidth: 1.5 }} // ✅ LÍNEA MÁS PROFESIONAL
              tickLine={false}
            />
            {/* 📊 BARRAS GRÁFICO VISIBLE - DEBEN SER IDÉNTICAS PARA PERFECTA ALINEACIÓN */}
            <Bar 
              dataKey="percentageAchieved" 
              fill={COLORS.achieved}
              barSize={barSize} // 🎯 BARRA LOGRADO VISIBLE - Usa valor de línea 478
              name="Logrado"
              radius={[2, 2, 2, 2]}
            />
            <Bar
              dataKey="expectedLevel"
              fill={COLORS.expected}
              name="Esperado" 
              barSize={1} // 🚫 BARRA ESPERADO VISIBLE - SÚPER DELGADA como línea (era 8)
              radius={[2, 2, 2, 2]}
            />
            {/* 🎯 SCATTER VISIBLE - SINCRONIZADO CON BARSIZE */}
            <Scatter 
              dataKey="previousYearLevel" 
              fill={COLORS.previousYear}
              name="Anterior"
              shape={<CustomVerticalRectangle width={5} height={barSize} />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const captureChart = async () => {
    if (chartRef.current) {
      try {
        // Verificar que el elemento tenga dimensiones válidas
        const rect = chartRef.current.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          console.error('El elemento chartRef no tiene dimensiones válidas');
          return null;
        }
        
        // Esperar tiempo OPTIMIZADO para renderizado completo
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const devicePixelRatio = window.devicePixelRatio || 1;
        
        // Usar dimensiones OPTIMIZADAS para máxima calidad de texto
        const captureWidth = 1400;  // Aumentado para mejor calidad de texto
        const captureHeight = 1000;  // Aumentado para mejor calidad de texto
        
        const canvas = await html2canvas(chartRef.current, {
          scale: 3.0, // Escala aumentada para máxima calidad de texto
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#FFFFFF',
          width: captureWidth,
          height: captureHeight,
          imageTimeout: 5000, // Más tiempo para renderizado completo
          // Configuración MÁXIMA para calidad profesional de texto
          onclone: (clonedDoc) => {
            // Asegurar que el elemento clonado tenga las dimensiones correctas
            const clonedElement = clonedDoc.querySelector('[data-chart-ref]') || clonedDoc.body.firstChild;
            if (clonedElement instanceof HTMLElement) {
              clonedElement.style.width = captureWidth + 'px';
              clonedElement.style.height = captureHeight + 'px';
              clonedElement.style.position = 'static';
              clonedElement.style.left = '0';
              clonedElement.style.top = '0';
              clonedElement.style.transform = 'none';
              clonedElement.style.transformOrigin = 'top left';
            }
            
            // MEJORAR CALIDAD DE TEXTO EN SVG - Configuración máxima
            const svgElements = clonedDoc.querySelectorAll('svg');
            svgElements.forEach((svg: Element) => {
              if (svg instanceof SVGElement) {
                // Configuración máxima para calidad de texto
                svg.style.fontFamily = 'Arial, Helvetica, sans-serif';
                svg.style.fontSize = '14px'; // Aumentado para mejor legibilidad
                svg.style.fontWeight = '600'; // Más bold para mejor definición
                svg.style.textRendering = 'optimizeLegibility';
                svg.style.shapeRendering = 'geometricPrecision';
                (svg.style as any).fontSmooth = 'always';
                (svg.style as any).webkitFontSmoothing = 'antialiased';
                (svg.style as any).mozOsxFontSmoothing = 'grayscale';
                svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
              }
            });
            
            // MEJORAR CALIDAD DE TEXTO EN ELEMENTOS TEXT - Configuración máxima
            const textElements = clonedDoc.querySelectorAll('text, tspan');
            Array.from(textElements).forEach((text: Element) => {
              if (text instanceof SVGTextElement || text instanceof SVGTSpanElement) {
                // Configuración máxima para calidad de texto
                text.style.fontFamily = 'Arial, Helvetica, sans-serif';
                text.style.fontSize = '14px'; // Aumentado para mejor legibilidad
                text.style.fontWeight = '600'; // Más bold para mejor definición
                text.style.textRendering = 'optimizeLegibility';
                (text.style as any).fontSmooth = 'always';
                (text.style as any).webkitFontSmoothing = 'antialiased';
                (text.style as any).mozOsxFontSmoothing = 'grayscale';
                text.style.fillOpacity = '1';
                text.style.strokeWidth = '0';
                // Asegurar que el texto sea nítido
                text.setAttribute('text-rendering', 'optimizeLegibility');
                text.setAttribute('font-smooth', 'always');
              }
            });

            // Optimizar TODOS los elementos gráficos para calidad máxima
            const graphicElements = clonedDoc.querySelectorAll('path, rect, circle, line, polygon');
            graphicElements.forEach((element: Element) => {
              if (element instanceof SVGElement) {
                element.style.shapeRendering = 'geometricPrecision';
                element.setAttribute('vector-effect', 'non-scaling-stroke');
                // Asegurar colores sólidos sin transparencia problemática
                const fill = element.getAttribute('fill');
                if (fill && fill !== 'none') {
                  element.style.fill = fill;
                  element.style.fillOpacity = '1';
                }
                const stroke = element.getAttribute('stroke');
                if (stroke && stroke !== 'none') {
                  element.style.stroke = stroke;
                  element.style.strokeWidth = element.getAttribute('stroke-width') || '1';
                  element.style.strokeOpacity = '1';
                }
              }
            });
            
            // Estilos CSS MÁXIMOS para calidad de texto
            const style = clonedDoc.createElement('style');
            style.textContent = `
              * {
                font-family: Arial, Helvetica, sans-serif !important;
                font-weight: 600 !important;
                text-rendering: optimizeLegibility !important;
                font-smooth: always !important;
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
              }
              svg {
                font-family: Arial, Helvetica, sans-serif !important;
                font-weight: 600 !important;
                font-size: 14px !important;
                text-rendering: optimizeLegibility !important;
                font-smooth: always !important;
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
              }
              text, tspan {
                font-family: Arial, Helvetica, sans-serif !important;
                font-weight: 600 !important;
                font-size: 14px !important;
                text-rendering: optimizeLegibility !important;
                font-smooth: always !important;
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
                fill-opacity: 1 !important;
                stroke-width: 0 !important;
              }
              .chart-container {
                font-family: Arial, Helvetica, sans-serif !important;
                font-weight: 600 !important;
                text-rendering: optimizeLegibility !important;
                font-smooth: always !important;
                -webkit-font-smoothing: antialiased !important;
                -moz-osx-font-smoothing: grayscale !important;
              }
            `;
            clonedDoc.head.appendChild(style);
          }
        });

        // Verificar que el canvas tenga contenido
        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error('El canvas generado no tiene dimensiones válidas');
        }

        // Crear canvas de MÁXIMA CALIDAD para texto nítido
        const optimizedCanvas = document.createElement('canvas');
        const ctx = optimizedCanvas.getContext('2d', { 
          alpha: false,
          desynchronized: false
        }) as CanvasRenderingContext2D | null;
        
        if (!ctx) {
          throw new Error('No se pudo crear el contexto del canvas');
        }

        // MANTENER dimensiones originales para máxima calidad de texto
        const targetWidth = canvas.width;
        const targetHeight = canvas.height;
        
        optimizedCanvas.width = targetWidth;
        optimizedCanvas.height = targetHeight;
        
        // Configuración de MÁXIMA CALIDAD para texto nítido
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';
        // Configuración adicional para calidad de texto (propiedades no estándar)
        (ctx as any).textRenderingOptimization = 'optimizeLegibility';
        
        // Fondo blanco sólido
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        
        // Dibujar imagen SIN reducir tamaño (1:1) para máxima calidad
        ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        // Convertir a PNG para máxima calidad de texto (sin pérdida)
        const imageFormat = 'image/png';
        
        return optimizedCanvas.toDataURL(imageFormat);
      } catch (error) {
        console.error('Error al capturar el gráfico:', error);
        return null;
      }
    }
    console.error('chartRef.current no está disponible');
    return null;
  };

  const generatePDF = async (
    student: Student,
    chartImage?: string,
    includeQualitativePage = true,
    chartCompression: 'MEDIUM' | 'NONE' = 'NONE'
  ) => {
    const pdf = new jsPDF({
      compress: false, // Desactivar compresión para máxima calidad
      unit: 'mm',
      format: [215.9, 279.4], // Formato Carta (Letter) explícito
      putOnlyUsedFonts: false, // Incluir todas las fuentes para mejor calidad de texto
      orientation: 'portrait',
      floatPrecision: 16 // Máxima precisión para mejor calidad
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const BG_COLOR = '#FFFFFF';
    const cornerRadius = 5;

    // Agregar versión en alguna esquina del PDF
    pdf.setFontSize(8);
    pdf.setTextColor(150);
    pdf.text(`v${APP_VERSION}`, pdf.internal.pageSize.getWidth() - 20, pdf.internal.pageSize.getHeight() - 10);
    
    // Función para añadir el fondo blanco y la barra lateral
    const addBackgroundAndSidebar = (isCoverPage = false) => {
      // Agregar fondo blanco
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Obtener colores de branding personalizados
      const brandingColors = getTenantBrandingColors();
      const primaryColor = brandingColors.primary;

      // Ajustar ancho y posición según si es portada o no
      const leftMargin = isCoverPage ? 10 : 0; // Más espacio en blanco a la izquierda en portada
      const blueBarWidth = isCoverPage ? 50 : 20; // Aumentado a 55 para portada (de 40 a 55), normal para otras páginas
      pdf.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b); // Color primario personalizado
      pdf.rect(leftMargin, 0, blueBarWidth, pageHeight, 'F');

      // Texto "Ciclo Amundsen" o "Ciclo Scott" vertical
      const cicloText = getCicloText(student.course);
      
      // Solo renderizar si hay texto de ciclo (solo para CPV)
      if (cicloText.length > 0) {
        // Convertir a formato con mayúscula inicial solamente
        const formattedCicloText = cicloText.charAt(0).toUpperCase() + cicloText.slice(1).toLowerCase();
        const fullText = `Ciclo ${formattedCicloText}`;
        
        pdf.setFontSize(isCoverPage ? 42 : 28); // Triple para portada (14*3=42), grande para internas
        pdf.setTextColor(180, 180, 180); // Gris claro
        pdf.setFont('helvetica', 'normal');
        
        if (isCoverPage) {
          // Para portada: texto vertical a la derecha de la barra, con margen desde el logo
          const logoWidth = 32;
          const logoHeight = logoWidth * 1.2;
          const logoY = 25; // Movido más abajo (de 9 a 25)
          
          // Ajustar margen según si es Amundsen (más largo) o Scott
          const isAmundsen = formattedCicloText.toLowerCase() === 'amundsen';
          const extraMargin = isAmundsen ? 30 : 0; // 30mm extra para Amundsen
          const logoBottomY = logoY + logoHeight + 95 + extraMargin; // Margen ajustado según ciclo
          
          const textX = leftMargin + blueBarWidth + 20; // 20mm de margen desde la barra
          const textY = logoBottomY; // Inicia desde el margen inferior del logo
          
          pdf.saveGraphicsState();
          pdf.text(fullText, textX, textY, {
            angle: 90 // Rotación vertical
          });
          pdf.restoreGraphicsState();
        } else {
          // Para páginas internas: texto vertical dentro de la barra
          const xPosition = blueBarWidth / 2 + 3;
          const bottomMargin = 30;
          const yPosition = pageHeight - bottomMargin;
          
          pdf.saveGraphicsState();
          pdf.text(fullText, xPosition, yPosition, {
            angle: 90
          });
          pdf.restoreGraphicsState();
        }
      }
    };

// Función para añadir el header a cada página
    const addHeader = async () => {
      const blueBarWidth = 20;
      const logoSizes = getTenantLogoSize();
      
      // Logo centrado en el borde de la barra lateral (mitad dentro, mitad fuera)
      try {
        const tenantLogo = getTenantLogo();
        const logoBase64 = await loadImageAsBase64(tenantLogo);
        const logoWidth = logoSizes.header.width; // Tamaño configurable
        const logoHeight = logoSizes.header.height;
        const logoX = blueBarWidth - (logoWidth / 2); // Centrado en el borde de la barra
        const logoY = 6; // Ajustado para el logo más grande
        pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'FAST');
      } catch (error) {
        console.error('Error al agregar el logo al header:', error);
      }
      
      // Línea vertical decorativa más a la derecha
      const lineX = 35;
      pdf.setDrawColor(0, 0, 0); // Negro oscuro
      pdf.setLineWidth(0.5);
      pdf.line(lineX, 12, lineX, 28);
      
      // Nombre y Curso en formato horizontal
      const textX = lineX + 5;
      
      // Nombre (label y valor en la misma línea)
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Negro oscuro
      pdf.text('Nombre alumno/a', textX, 18);
      
      const labelWidth = pdf.getStringUnitWidth('Nombre alumno/a') * 9 / pdf.internal.scaleFactor;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(student.name, textX + labelWidth + 3, 18);
      
      // Curso (label y valor en la misma línea)
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0); // Negro oscuro
      pdf.text('Curso', textX, 25);
      
      const cursoLabelWidth = pdf.getStringUnitWidth('Curso') * 9 / pdf.internal.scaleFactor;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(student.course, textX + cursoLabelWidth + 3, 25);
    };

    // Portada
    addBackgroundAndSidebar(true); // true indica que es la portada
    
    // Logo en portada - sobre la barra lateral
    try {
      const tenantLogo = getTenantLogo();
      const logoBase64 = await loadImageAsBase64(tenantLogo);
      const logoSizes = getTenantLogoSize();
      const leftMargin = 10; // Mismo margen que la barra
      const blueBarWidth = 50; // Mismo ancho que la barra de portada
      const logoWidth = logoSizes.cover.width; // Tamaño configurable
      const logoHeight = logoSizes.cover.height;
      const logoX = leftMargin + blueBarWidth - (logoWidth / 2); // Centrado en el borde de la barra
      const logoY = 20; // Ajustado para el logo más grande
      const logoBottomY = logoY + logoHeight; // Posición inferior del logo
      pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'FAST');
    } catch (error) {
      console.error('Error al agregar el logo a la portada:', error);
    }
    
    // Línea vertical de énfasis en el cuadrante derecho inferior
    const verticalLineX = pageWidth / 2 + 10; // Más a la derecha del centro
    
    // Contenido en el cuadrante derecho inferior
    const contentStartX = verticalLineX + 15; // Margen desde la línea vertical
    const contentStartY = pageHeight / 2 + 30; // Posición de inicio del título (ajustado)
    const lineSpacing = 15; // Aumentado de 13 a 15 para más espacio
    let currentY = contentStartY;
    
    // Línea vertical inicia a la misma altura que el título y termina en el borde inferior
    const verticalLineYStart = contentStartY - 5; // Inicia a la altura del título
    const verticalLineYEnd = pageHeight; // Termina en el borde inferior sin margen
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(1);
    pdf.line(verticalLineX, verticalLineYStart, verticalLineX, verticalLineYEnd);
    
    // Título: cada palabra en una línea
    pdf.setFontSize(32); // Aumentado de 28 a 32
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    
    pdf.text('Informe', contentStartX, currentY);
    currentY += lineSpacing;
    
    pdf.text('Pedagógico', contentStartX, currentY);
    currentY += lineSpacing;
    
    pdf.text('Integral', contentStartX, currentY);
    currentY += lineSpacing + 8; // Espacio extra antes de la información del alumno
    
    // Información del alumno - más grande
    pdf.setFontSize(11); // Aumentado de 9 a 11
    pdf.setFont('helvetica', 'normal');
    
    pdf.text(`Nombre alumno/a`, contentStartX, currentY);
    currentY += 5;
    pdf.text(student.name, contentStartX, currentY);
    currentY += 9; // Aumentado de 7 a 9
    
    pdf.text('Curso', contentStartX, currentY);
    currentY += 5;
    pdf.text(student.course, contentStartX, currentY);
    currentY += 9; // Aumentado de 7 a 9

    pdf.text('Año', contentStartX, currentY);
    currentY += 5;
    pdf.text(String(new Date().getFullYear()), contentStartX, currentY);
    currentY += 9;
    
    const teacherInfo = await getTeacherInfoForVisualization(student.course);
    const spanish = teacherInfo?.spanishTeacher?.trim();
    const english = teacherInfo?.englishTeacher?.trim();
    const additionalTeachersExport = teacherInfo?.additionalTeachers || [];
    
    // Crear lista de todos los profesores únicos
    const allTeachersExport: string[] = [];
    if (spanish) allTeachersExport.push(spanish);
    if (english && english !== spanish) allTeachersExport.push(english);
    // Agregar profesores adicionales
    additionalTeachersExport.forEach((teacher: { name: string; role: string }) => {
      if (teacher.name && !allTeachersExport.includes(teacher.name)) {
        allTeachersExport.push(teacher.name);
      }
    });

    const teachersLabel = allTeachersExport.length > 1 ? 'Docentes' : 'Docente';
    pdf.text(teachersLabel, contentStartX, currentY);
    currentY += 5;

    const teachersText = allTeachersExport.length > 0
      ? allTeachersExport.join(' - ')
      : 'No hay información disponible';

    // Dividir el texto si es muy largo para que no se salga del margen
    const maxWidth = pageWidth - contentStartX - 10;
    const teacherLines = pdf.splitTextToSize(teachersText, maxWidth);
    teacherLines.forEach((line: string, index: number) => {
      pdf.text(line, contentStartX, currentY + (index * 5)); // Aumentado de 4 a 5
    });

    // Página del gráfico con estilo original del colegio (siempre incluida)
    pdf.addPage();
    addBackgroundAndSidebar();
    await addHeader();

    if (chartImage) {
      // SIN TÍTULO - usar todo el espacio disponible

      // Configuración MÁXIMA para usar todo el alto disponible
      const chartAreaTop = 40; // Mínimo espacio desde arriba (sin título)
      const chartAreaBottom = pageHeight - 20; // Mínimo espacio abajo
      const maxChartHeight = chartAreaBottom - chartAreaTop;

      // Márgenes optimizados para usar TODO el espacio
      const sidebarWidth = 20; // Ancho de la barra lateral
      const leftMargin = sidebarWidth + 10; // Mínimo después de la barra
      const rightMargin = 10; // Margen derecho mínimo
      const maxChartWidth = pageWidth - leftMargin - rightMargin;

      // Usar MÁXIMO espacio disponible (casi el 100%)
      const actualChartWidth = maxChartWidth * 0.98; // 98% del ancho
      const actualChartHeight = maxChartHeight * 0.95; // 95% de la altura total

      // Posicionar el gráfico centrado horizontalmente
      const chartX = leftMargin + (maxChartWidth - actualChartWidth) / 2;
      const chartY = chartAreaTop;

      // Agregar imagen del gráfico con tamaño MÁXIMO y máxima calidad
      const imageFormat = chartImage.startsWith('data:image/png') ? 'PNG' : 'JPEG';

      pdf.addImage(chartImage, imageFormat, chartX, chartY, actualChartWidth, actualChartHeight, '', chartCompression);
    } else {
      // Mensaje centrado si no hay gráfico
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(150, 150, 150);
      pdf.text('No se pudo cargar el gráfico', pageWidth / 2, pageHeight / 2, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
    }

    if (includeQualitativePage) {
      // Invitación al trabajo colaborativo (Análisis Cualitativa)
      pdf.addPage();
      addBackgroundAndSidebar();
      await addHeader();

      const titleY = 50;
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Invitación al trabajo colaborativo', 30, titleY);
      
      // Configuración del texto
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      // Normalizar a NFC para evitar problemas con tildes separadas (e.g. n + ~) que rompen la justificación
      const description = getQualitativeDescription(student).normalize('NFC');
      const textLeftMargin = 30;
      const textRightMargin = 30; // Aumentado de 10 a 30 para más margen
      const textPadding = 10;
      const containerWidth = pageWidth - (textLeftMargin + textRightMargin);
      const textAreaWidth = containerWidth - (2 * textPadding); // Ancho real disponible para texto
      
      // Pre-procesar el texto para dividir por párrafos y luego por ancho
      const rawParagraphs = description.split(/\r?\n/);
      const splitText: { text: string; isLast: boolean }[] = [];

      rawParagraphs.forEach((paragraph) => {
        // Normalizar espacios y eliminar espacios al inicio/final del párrafo
        const trimmedParagraph = paragraph.trim().replace(/\s+/g, ' ');
        
        // Ignorar párrafos vacíos si se desea
        if (!trimmedParagraph) {
           return;
        }
        const lines = pdf.splitTextToSize(trimmedParagraph, textAreaWidth);
        lines.forEach((line: string, idx: number) => {
          // Asegurarse de que la línea no tenga espacios extraños al inicio/final
          const cleanLine = line.trim();
          if (cleanLine) {
              splitText.push({
                  text: cleanLine,
                  isLast: idx === lines.length - 1
              });
          }
        });
        // Agregar un espacio vertical entre párrafos reales si es necesario
        // Opcional: splitText.push({ text: '', isLast: true }); 
      });

      const lineHeight = 5.5;
      let qualitativeY = titleY + 20;
      
      // Calcular altura total del texto en la página actual
      const calculateTextHeight = (lines: { text: string; isLast: boolean }[], startIndex: number, maxY: number): number => {
        let height = textPadding * 2;
        let currentLine = startIndex;
        while (currentLine < lines.length) {
          const nextHeight = height + lineHeight;
          if (qualitativeY + nextHeight > maxY) break;
          height = nextHeight;
          currentLine++;
        }
        return height;
      };

      // Procesar el texto página por página
      let currentIndex = 0;
      while (currentIndex < splitText.length) {
        const maxY = pageHeight - margin;
        const textHeight = calculateTextHeight(splitText, currentIndex, maxY);
        
        // Dibujar el contenedor
        pdf.setFillColor(BG_COLOR);
        pdf.setDrawColor(0, 0, 0);
        pdf.roundedRect(textLeftMargin, qualitativeY, containerWidth, textHeight, cornerRadius, cornerRadius, 'S');
        
        // Escribir el texto dentro del contenedor con mejor alineación
        let y = qualitativeY + textPadding;
        while (currentIndex < splitText.length && y + lineHeight <= qualitativeY + textHeight - textPadding) {
          const lineObj = splitText[currentIndex];
          const justifiedLine = justifyText(lineObj.text.trim(), textAreaWidth, pdf, lineObj.isLast);
          // Evitar re-wrap de jsPDF (ya usamos splitTextToSize)
          pdf.text(justifiedLine, textLeftMargin + textPadding, y, { align: 'left' });
          y += lineHeight;
          currentIndex++;
        }

        // Si quedan más líneas, crear nueva página
        if (currentIndex < splitText.length) {
          pdf.addPage();
          addBackgroundAndSidebar();
          await addHeader();
          qualitativeY = margin + 15;
        }
      }
    }

    const descriptionBlocks = getVisibleSubdimensionDescriptionsForPdf(
      student,
      tenantKey
    );
    if (descriptionBlocks.length > 0) {
      await renderMethodologicalNoteTable(pdf, {
        sectionTitle: 'Descripción de Subdimensiones',
        blocks: descriptionBlocks.map((block) => ({
          title: block.displayTitle,
          paragraphs: block.paragraphs,
        })),
        layout: {
          pageWidth,
          pageHeight,
          margin,
          textLeftMargin: 32,
          textRightMargin: 10,
        },
        setupPage: async () => {
          addBackgroundAndSidebar();
          await addHeader();
        },
      });
    }

    // Generar nombre de archivo: RUT-informe_integral.pdf
    const rutLimpio = extractRutFromStudent(student);
    pdf.save(`${rutLimpio}-informe_integral.pdf`);
  };

  const handleExportToPDF = async (includeQualitativePage: boolean) => {
    if (selectedStudentIds.length > 0) {
      const student = findStudentById(students, selectedStudentIds[0]);
      if (student) {
        try {
          setIsExportingPDF(true);
          
          const profile = getChartCaptureProfile(useUltraHdCharts);
          const chartImage = await captureNewChart(student, useUltraHdCharts);
          await generatePDF(student, chartImage || undefined, includeQualitativePage, profile.pdfImageCompression);
          
        } catch (error) {
          console.error('Error al exportar a PDF:', error);
          alert('Hubo un error al generar el PDF. Por favor, inténtelo de nuevo.');
        } finally {
          setIsExportingPDF(false);
        }
      } else {
        alert('No se encontró el estudiante seleccionado. Intente seleccionarlo nuevamente.');
      }
    }
  };

  // Captura del gráfico: mismo layout siempre; alta calidad solo vía supersampling y JPEG
  const captureNewChart = async (student: Student, highQuality = true): Promise<string | null> => {
    const profile = getChartCaptureProfile(highQuality);

    return new Promise((resolve) => {
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = `${profile.containerWidth}px`;
      tempContainer.style.height = `${profile.containerHeight}px`;
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '10px';
      tempContainer.style.transform = 'scale(1.0)';
      tempContainer.style.transformOrigin = 'top left';
      tempContainer.className = 'temp-chart-container';
      
      document.body.appendChild(tempContainer);
      
      import('react-dom/client').then(({ createRoot }) => {
        const root = createRoot(tempContainer);
        root.render(
          React.createElement(StudentDimensionsChart, {
            student: student,
            showLegend: true,
            dimensionCategories: filteredDimensionCategories,
            height: profile.chartHeight,
            showSubdimensions: true,
            pdfMode: true,
            tenantKey: tenantKey,
            showPreviousYearLevel: true,
          })
        );
        
        setTimeout(async () => {
          try {
            const canvas = await html2canvas(tempContainer, {
              backgroundColor: '#ffffff',
              scale: profile.scale,
              width: profile.captureWidth,
              height: profile.captureHeight,
              useCORS: true,
              allowTaint: false,
              foreignObjectRendering: false,
              imageTimeout: highQuality ? 8000 : 0,
              logging: false,
              removeContainer: false,
            });
            const chartImage = profile.outputFormat === 'jpeg'
              ? canvas.toDataURL('image/jpeg', profile.outputQuality)
              : canvas.toDataURL('image/png', profile.outputQuality);
            
            root.unmount();
            while (tempContainer.firstChild) {
              tempContainer.removeChild(tempContainer.firstChild);
            }
            document.body.removeChild(tempContainer);
            
            if (window.gc && typeof window.gc === 'function') {
              try {
                window.gc();
              } catch (e) {
                // Ignorar si no está disponible
              }
            }
            
            resolve(chartImage);
          } catch (error) {
            console.error('Error capturando gráfico:', error);
            root.unmount();
            while (tempContainer.firstChild) {
              tempContainer.removeChild(tempContainer.firstChild);
            }
            document.body.removeChild(tempContainer);
            resolve(null);
          }
        }, profile.waitMs);
      });
    });
  };



  // Función mejorada para renovación automática de token durante exportación
  const refreshTokenIfNeeded = async (force: boolean = false): Promise<boolean> => {
    const now = Date.now();
    const timeSinceLastRefresh = now - sessionStatus.lastRefresh;
    
    // Renovar token cada 20 minutos o si se fuerza
    if (force || timeSinceLastRefresh > 20 * 60 * 1000) {
      try {
        setSessionStatus(prev => ({ ...prev, isRefreshing: true }));
        console.log('🔄 Renovando token durante exportación...');
        
        // Usar el tokenManager para renovar
        const currentToken = localStorage.getItem('token');
        if (currentToken && !tokenManager.shouldClearSession()) {
          // Token aún válido, actualizar timestamp
          setSessionStatus(prev => ({
            ...prev,
            lastRefresh: now,
            refreshCount: prev.refreshCount + 1,
            isRefreshing: false
          }));
          return true;
        }
        
        // Intentar renovación manual si es necesario
        const response = await fetch('/api/refresh-token', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.token) {
            localStorage.setItem('token', data.token);
            setSessionStatus(prev => ({
              ...prev,
              lastRefresh: now,
              refreshCount: prev.refreshCount + 1,
              isRefreshing: false
            }));
            console.log('✅ Token renovado exitosamente');
            return true;
          }
        }
        
        console.warn('⚠️ No se pudo renovar el token');
        return false;
      } catch (error) {
        console.error('❌ Error renovando token:', error);
        setSessionStatus(prev => ({ ...prev, isRefreshing: false }));
        return false;
      }
    }
    
    return true;
  };

  // Función para procesar un lote de estudiantes
  const processBatch = async (students: Student[], startIndex: number, batchSize: number): Promise<{ success: number; errors: string[] }> => {
    const results = { success: 0, errors: [] as string[] };
    const endIndex = Math.min(startIndex + batchSize, students.length);
    
    for (let i = startIndex; i < endIndex; i++) {
      // Verificar si está pausado
      if (exportStateRef.current.isPaused) {
        console.log('⏸️ Exportación pausada');
        break;
      }
      
      const student = students[i];
      
      try {
        console.log(`📄 Procesando ${i + 1}/${students.length}: ${student.name} (${student.course})`);
        
        // Actualizar estado
        setExportState(prev => ({
          ...prev,
          currentIndex: i + 1,
          processedCount: prev.processedCount + 1
        }));
        
        const profile = getChartCaptureProfile(useUltraHdCharts);
        const chartImage = await captureNewChart(student, useUltraHdCharts);
        await generatePDF(student, chartImage || undefined, true, profile.pdfImageCompression);
        
        results.success++;
        
        // Pequeña pausa entre estudiantes para evitar problemas de memoria
        await new Promise(resolve => setTimeout(resolve, 300));
        
      } catch (error) {
        const errorMsg = `Error procesando ${student.name}: ${error instanceof Error ? error.message : 'Error desconocido'}`;
        console.error(`❌ ${errorMsg}`);
        results.errors.push(errorMsg);
        
        setExportState(prev => ({
          ...prev,
          errorCount: prev.errorCount + 1,
          errors: [...prev.errors, errorMsg]
        }));
      }
      
      // Actualizar progreso
      setExportProgress(((i + 1) / students.length) * 100);
      
      // Actualizar estimación de tiempo
      const elapsed = Date.now() - (exportStateRef.current.startTime || Date.now());
      const rate = (i + 1) / elapsed;
      const remaining = (students.length - (i + 1)) / rate;
      
      setExportState(prev => ({
        ...prev,
        estimatedTimeRemaining: remaining
      }));
    }
    
    return results;
  };

  // Función principal mejorada para exportación masiva
  const handleExportPDFMassive = async () => {
    const studentsToExport = filteredStudents;
    const BATCH_SIZE = 5; // Procesar 5 estudiantes por lote
    
    if (studentsToExport.length === 0) {
      alert('No hay estudiantes para exportar.');
      return;
    }
    
    // Inicializar estado
    setIsExportingMassive(true);
    setExportProgress(0);
    setExportState({
      isRunning: true,
      isPaused: false,
      currentIndex: 0,
      totalCount: studentsToExport.length,
      processedCount: 0,
      errorCount: 0,
      errors: [],
      startTime: Date.now(),
      estimatedTimeRemaining: 0
    });
    
    console.log(`🚀 Iniciando exportación masiva mejorada para ${studentsToExport.length} estudiantes (lotes de ${BATCH_SIZE})`);
    
    // Renovar token inicial
    await refreshTokenIfNeeded(true);
    
    let currentIndex = 0;
    let totalSuccess = 0;
    let totalErrors: string[] = [];
    
    // Función recursiva para procesar lotes en background
    const processNextBatch = async (): Promise<void> => {
      return new Promise((resolve) => {
        // Usar requestAnimationFrame para no bloquear la UI
        requestAnimationFrame(async () => {
          try {
            // Verificar si está pausado o detenido
            if (exportStateRef.current.isPaused) {
              console.log('⏸️ Exportación pausada, esperando...');
              setTimeout(() => processNextBatch().then(resolve), 1000);
              return;
            }
            
            if (!exportStateRef.current.isRunning) {
              console.log('🛑 Exportación detenida');
              resolve();
              return;
            }
            
            // Renovar token cada 10 estudiantes
            if (currentIndex > 0 && currentIndex % 10 === 0) {
              const tokenRefreshed = await refreshTokenIfNeeded();
              if (!tokenRefreshed) {
                console.error('❌ No se pudo renovar el token, deteniendo exportación');
                setExportState(prev => ({ ...prev, isRunning: false }));
                resolve();
                return;
              }
            }
            
            // Procesar lote
            const batchResults = await processBatch(studentsToExport, currentIndex, BATCH_SIZE);
            totalSuccess += batchResults.success;
            totalErrors = [...totalErrors, ...batchResults.errors];
            
            currentIndex += BATCH_SIZE;
            
            // Verificar si quedan más estudiantes
            if (currentIndex < studentsToExport.length && exportStateRef.current.isRunning) {
              // Continuar con el siguiente lote
              processNextBatch().then(resolve);
            } else {
              // Completado
              resolve();
            }
          } catch (error) {
            console.error('❌ Error en procesamiento por lotes:', error);
            resolve();
          }
        });
      });
    };
    
    // Iniciar procesamiento
    try {
      await processNextBatch();
      
      // Finalizar
      setIsExportingMassive(false);
      setExportState(prev => ({ ...prev, isRunning: false }));
      
      const finalMessage = `✅ Exportación completada!\n\n` +
        `📊 Resumen:\n` +
        `• Total procesados: ${totalSuccess}/${studentsToExport.length}\n` +
        `• Errores: ${totalErrors.length}\n` +
        `• Token renovado: ${sessionStatus.refreshCount} veces\n` +
        `• Tiempo total: ${Math.round((Date.now() - (exportState.startTime || 0)) / 1000)} segundos`;
      
      console.log(finalMessage);
      alert(finalMessage);
      
      if (totalErrors.length > 0) {
        console.log('❌ Errores encontrados:', totalErrors);
      }
      
    } catch (error) {
      console.error('❌ Error general en exportación masiva:', error);
      setIsExportingMassive(false);
      setExportState(prev => ({ ...prev, isRunning: false }));
      alert('Error durante la exportación masiva. Revisa la consola para más detalles.');
    }
  };

  // Función para pausar/reanudar exportación
  const togglePauseExport = () => {
    setExportState(prev => ({
      ...prev,
      isPaused: !prev.isPaused
    }));
  };

  // Función para detener exportación
  const stopExport = () => {
    setExportState(prev => ({
      ...prev,
      isRunning: false,
      isPaused: false
    }));
    setIsExportingMassive(false);
  };

  // Modificar updateChartDataForStudent para ser más preciso - v3
  const updateChartDataForStudent = async (student: Student) => {
    let data: any[] = [];
    
    // Obtener información de docentes desde MongoDB
    const teacherInfo = await getTeacherInfoForVisualization(student.course);
    if (teacherInfo) {
      const additionalT = teacherInfo.additionalTeachers || [];
      setCurrentTeachers({
        spanish: teacherInfo.spanishTeacher,
        english: teacherInfo.englishTeacher,
        additional: additionalT
      });
    } else {
      setCurrentTeachers(null);
    }
    
    // Obtener dimensiones base y filtrar según el curso del estudiante
    const baseDimensions = getAllDimensions(tenantKey).map(dim => ({
      id: dim.id,
      name: dim.name,
      skills: dim.dimension.skills
    }));
    
    const dimensionsForChart = filterDimensionsByCourse(baseDimensions, student.course);

    const birthDate = new Date(student.birthDate.split('-').reverse().join('-'));
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear() - 
      (today.getMonth() < birthDate.getMonth() || 
      (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);

    const anchoredAge = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    dimensionsForChart.forEach(dim => {
      // 🎯 NUEVO SISTEMA: Usar cálculo por tramos de edad
      const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, student.course, student.skillLevels);

      // Usar el porcentaje calculado por tramos
      const percentageAchieved = tramoResult.percentage;

      // 🎯 CÁLCULO HISTÓRICO REAL (si existe data)
      let previousYearLevelValue = calculatePreviousYearPercentage(anchoredAge);
      if (student.previousYearLevels && student.previousYearLevels.length > 0) {
        const historicalLevels = student.previousYearLevels
          .filter(pl => pl.dimensionId === dim.id)
          .map(pl => ({ dimensionId: pl.dimensionId, skillId: pl.skillId, level: pl.level }));
        
        if (historicalLevels.length > 0) {
          // Estimar curso anterior para el denominador
          const courseBase = Object.keys(COURSE_TO_AGE_MAP).find(base => student.course && student.course.includes(base));
          const currentAge = COURSE_TO_AGE_MAP[courseBase as keyof typeof COURSE_TO_AGE_MAP] || 3;
          const prevAge = Math.max(3, currentAge - 1);
          const prevCourse = Object.entries(COURSE_TO_AGE_MAP).find(([_, age]) => age === prevAge)?.[0] || "Medio Mayor";
          
          const historicalResult = calculateProgressByAgeTramos(dim.id, dim.name, prevCourse, historicalLevels);
          previousYearLevelValue = historicalResult.percentage;
        }
      }

      data.push({
        name: dim.name,
        studentName: student.name,
        percentageAchieved: Math.round(percentageAchieved),
        expectedLevel: calculateExpectedPercentage(anchoredAge),
        previousYearLevel: Math.round(previousYearLevelValue)
      });
    });

    console.log(`Datos actualizados para ${student.name}:`, data);
    return data;
  };

  // Modificar el useEffect que maneja la selección de estudiantes
  useEffect(() => {
    const updateStudentData = async () => {
      if (selectedStudentIds.length > 0) {
        const selectedStudent = findStudentById(students, selectedStudentIds[0]);
        
        if (selectedStudent) {
          console.log(`Actualizando datos para estudiante seleccionado: ${selectedStudent.name}`);
          const newChartData = await updateChartDataForStudent(selectedStudent);
          setChartData(newChartData);
          // updateTeachersInfo ya no es necesario porque updateChartDataForStudent maneja los docentes
        }
      }
    };
    updateStudentData();
  }, [selectedStudentIds, students]);

  // Agregar un efecto de limpieza de caché
  useEffect(() => {
    const clearCache = async () => {
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
          console.log('Cache cleared successfully');
        } catch (err) {
          console.error('Error clearing cache:', err);
        }
      }
    };
    
    clearCache();
  }, []);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const calculateStudentStats = (student: Student) => {
    // Obtener dimensiones base y filtrar según el curso del estudiante
    const baseDimensionsForStats = getAllDimensions(tenantKey).map(dim => ({
      id: dim.id,
      name: dim.name,
      skills: dim.dimension.skills
    }));
    
    const dimensionsForStats = filterDimensionsByCourse(baseDimensionsForStats, student.course);

    const dimensions = dimensionsForStats.map(dim => {
      // Obtener los skillLevels del estudiante para esta dimensión
      const dimensionSkillLevels = student.skillLevels.filter(sl => sl.dimensionId === dim.id);

      // 🎯 NUEVO SISTEMA: Usar cálculo por tramos de edad
      const tramoResult = calculateProgressByAgeTramos(dim.id, dim.name, student.course, student.skillLevels);

      // Usar el porcentaje calculado por tramos
      const completeness = tramoResult.percentage;

      // Calcular promedio como porcentaje
      const averageLevel = completeness;

      return {
        ...dim,
        completeness,
        averageLevel,
        skillsCompleted: tramoResult.achievedTotal,
        totalSkills: tramoResult.expectedTotal,
        skillLevels: dimensionSkillLevels
      } as any;
    });

    const overallCompleteness = dimensions.reduce((sum, dim) => sum + dim.completeness, 0) / dimensions.length;
    const overallAverage = dimensions.reduce((sum, dim) => sum + dim.averageLevel, 0) / dimensions.length;

    return {
      dimensions,
      overallCompleteness,
      overallAverage,
      totalSkills: dimensions.reduce((sum, dim) => sum + dim.totalSkills, 0),
      completedSkills: dimensions.reduce((sum, dim) => sum + dim.skillsCompleted, 0)
    };
  };

  const renderStudentPreview = (student: Student) => {
    const stats = calculateStudentStats(student);
    // Usamos el estado currentTeachers que se carga desde MongoDB en updateChartDataForStudent
    
    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header del Preview */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <FaUserGraduate className="text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{student.name}</h2>
                <p className="text-blue-100">Informe Pedagógico Integral {new Date().getFullYear()}</p>
              </div>
            </div>
            {/* <div className="text-right">
              <div className="bg-white/20 rounded-lg p-3">
                <div className="text-xs text-blue-100">Progreso General</div>
                <div className="text-2xl font-bold">{stats.overallAverage.toFixed(1)}%</div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Información Básica */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <FaGraduationCap className="text-blue-600" />
                <span className="font-medium text-gray-700">Información Académica</span>
              </div>
              <div className="space-y-1 text-sm">
                <div><span className="font-medium">Curso:</span> {student.course}</div>
                <div><span className="font-medium">Fecha:</span> {new Date().toLocaleDateString('es-ES')}</div>
              </div>
            </div>
            
            {currentTeachers && (
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FaUsers className="text-green-600" />
                  <span className="font-medium text-gray-700">Equipo Docente</span>
                </div>
                <div className="space-y-1 text-sm">
                  {currentTeachers.spanish && (
                    <div><span className="font-medium">Principal:</span> {currentTeachers.spanish}</div>
                  )}
                  {currentTeachers.english && currentTeachers.english !== currentTeachers.spanish && (
                    <div><span className="font-medium">Secundario:</span> {currentTeachers.english}</div>
                  )}
                  {currentTeachers.additional && currentTeachers.additional.length > 0 && (
                    currentTeachers.additional.map((teacher, index) => (
                      <div key={index}>
                        <span className="font-medium">{teacher.role || 'Adicional'}:</span> {teacher.name}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <FaChartBar className="text-purple-600" />
                <span className="font-medium text-gray-700">Estadísticas</span>
              </div>
              <div className="space-y-1 text-sm">
                <div><span className="font-medium">Evaluado:</span> {stats.overallCompleteness.toFixed(1)}%</div>
                <div><span className="font-medium">Habilidades evaluadas:</span> {stats.completedSkills}/{stats.totalSkills}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Descripción Cualitativa */}
        <div className="p-6 border-t border-gray-200">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <FaInfoCircle className="mr-2 text-blue-600" />
            Descripción Cualitativa
          </h3>
          <div className="bg-blue-50 rounded-lg p-4 max-w-full overflow-hidden">
            <p className="text-gray-700 leading-relaxed text-justify break-words whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
              {getQualitativeDescription(student) || "Sin descripción cualitativa disponible"}
            </p>
          </div>
        </div>

        {/* Gráfico de Dimensiones Mejorado */}
        <div className="p-6 border-t border-gray-200">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <FaChartBar className="mr-2 text-purple-600" />
            Resumen de Dimensiones
          </h3>
          <div className="text-sm text-gray-600 mb-4">
            Desarrollo de {student.name} por dimensiones
          </div>
          <StudentDimensionsChart 
            student={student} 
            showLegend={true} 
            height={600} 
            tenantKey={tenantKey}
            dimensionCategories={filteredDimensionCategories}
            showPreviousYearLevel={true}
          />
        </div>
      </div>
    );
  };

  const handlePreviewStudent = async (student: Student) => {
    setSelectedStudentForPreview(student);
    
    // Asegurar que el estudiante esté seleccionado para que el gráfico esté disponible
    setSelectedStudentIds([student.id]);
    // Ya no necesitamos setViewMode
    
    // Actualizar los datos del gráfico para este estudiante
    const newChartData = await updateChartDataForStudent(student);
    setChartData(newChartData);
    // updateTeachersInfo ya no es necesario porque updateChartDataForStudent maneja los docentes
    
    setShowPreview(true);
  };

  const handleExportFromPreview = async (includeQualitativePage: boolean) => {
    if (selectedStudentForPreview) {
      try {
        setIsExportingPDF(true);
        
        const profile = getChartCaptureProfile(useUltraHdCharts);
        const chartImage = await captureNewChart(selectedStudentForPreview, useUltraHdCharts);
        await generatePDF(selectedStudentForPreview, chartImage || undefined, includeQualitativePage, profile.pdfImageCompression);
        
      } catch (error) {
        console.error('Error al exportar a PDF desde vista previa:', error);
        alert('Hubo un error al generar el PDF. Por favor, inténtelo de nuevo.');
      } finally {
        setIsExportingPDF(false);
      }
    }
  };



  return (
    <SimpleTutorialSection 
      section="exportacion"
      onDimensionSelect={onDimensionSelect}
      onViewModeChange={onViewModeChange}
    >
      <div className="space-y-6 export-options">
      {/* Header reorganizado */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-full">
              <FaDownload className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Exportación de Resultados</h1>
              <p className="text-gray-600">Selecciona un estudiante y exporta su informe pedagógico</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex space-x-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <FaUsers className="text-blue-600 mx-auto mb-1" />
              <div className="text-sm text-blue-600">Estudiantes</div>
              <div className="text-lg font-bold text-blue-700">{filteredStudents.length}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <FaChartBar className="text-green-600 mx-auto mb-1" />
              <div className="text-sm text-green-600">Dimensiones</div>
              <div className="text-lg font-bold text-green-700">11</div>
            </div>
          </div>
        </div>

        {/* Controles y opciones de exportación */}
        <div className="export-options flex items-center justify-between flex-wrap gap-4">
          {/* Selector de curso */}
          <div className="flex items-center space-x-4">
            <label className="font-semibold text-gray-700">Curso:</label>
            <select
              value={selectedCourse}
              onChange={(e) => handleCourseSelect(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {courses.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          
          {/* Opciones de exportación */}
          <div className="flex items-center space-x-3">
            {/* Exportación masiva */}
            <div className="flex items-center space-x-2 border-r border-gray-200 pr-3">
              <span className="text-sm font-medium text-gray-600">Exportación masiva:</span>
              <button
                onClick={() => exportToExcel(filteredStudents.map((student) => {
                  const studentData: { [key: string]: any } = { 
                    Estudiante: student.name,
                    Curso: student.course,
                    'Fecha de Nacimiento': student.birthDate
                  };
                  // Agregar skillLevels como columnas
                  student.skillLevels.forEach((skillLevel) => {
                    studentData[`Dim${skillLevel.dimensionId}_Skill${skillLevel.skillId}`] = 
                      skillLevel.level !== null ? `Nivel ${skillLevel.level + 1}` : 'No evaluado';
                  });
                  studentData["Descripción Cualitativa"] = student.qualitativeDescription || '';
                  return studentData;
                }))}
                className="flex items-center space-x-1 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors shadow-sm text-sm"
                title="Exportar estudiantes del curso seleccionado a Excel"
              >
                <FaFileExcel />
                <span>Excel</span>
              </button>
              <div className="relative" ref={massPdfMenuRef}>
                <button
                  onClick={() => setShowMassPdfMenu((open) => !open)}
                  className="flex items-center space-x-1 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors shadow-sm text-sm"
                  title="Exportar PDFs por cursos"
                >
                  <FaFolderOpen />
                  <span>PDF</span>
                  <FaChevronDown className={`text-xs transition-transform ${showMassPdfMenu ? 'rotate-180' : ''}`} />
                </button>
                {showMassPdfMenu && (
                  <div className="absolute top-full left-0 mt-1 z-50 min-w-[240px] bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                    <button
                      onClick={() => openMassExportModal(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Con descripción cualitativa
                    </button>
                    <button
                      onClick={() => openMassExportModal(false)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Sin descripción cualitativa
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Exportación individual - visible siempre pero deshabilitada si no hay selección */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-600">Exportación individual:</span>
              <div className="relative" ref={individualPdfMenuRef}>
                <button 
                  onClick={() => {
                    if (selectedStudentIds.length > 0 && !isExportingPDF) {
                      setShowIndividualPdfMenu((open) => !open);
                    }
                  }}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-all duration-200 shadow-sm text-sm ${
                    selectedStudentIds.length > 0 && !isExportingPDF
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={selectedStudentIds.length === 0 || isExportingPDF}
                  title="Exportar PDF del estudiante seleccionado"
                >
                  {isExportingPDF ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <>
                      <FaFilePdf />
                      <span>PDF</span>
                      <FaChevronDown className={`text-xs transition-transform ${showIndividualPdfMenu ? 'rotate-180' : ''}`} />
                    </>
                  )}
                </button>
                {showIndividualPdfMenu && selectedStudentIds.length > 0 && !isExportingPDF && (
                  <div className="absolute top-full left-0 mt-1 z-50 min-w-[240px] bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                    <button
                      onClick={() => {
                        setShowIndividualPdfMenu(false);
                        handleExportToPDF(true);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Con descripción cualitativa
                    </button>
                    <button
                      onClick={() => {
                        setShowIndividualPdfMenu(false);
                        handleExportToPDF(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Sin descripción cualitativa
                    </button>
                  </div>
                )}
              </div>
              <button 
                onClick={() => {
                  const selectedStudent = findStudentById(filteredStudents, selectedStudentIds[0]);
                  if (selectedStudent) {
                    handlePreviewStudent(selectedStudent);
                  }
                }}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-all duration-200 shadow-sm text-sm ${
                  selectedStudentIds.length > 0
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                disabled={selectedStudentIds.length === 0}
                title="Vista previa del informe"
              >
                <FaEye />
                <span>Vista Previa</span>
              </button>
            </div>

            <div className="flex items-center border-l border-gray-200 pl-3">
              <label
                className="flex items-center space-x-2 cursor-pointer text-sm text-gray-700"
                title="Solo afecta la página de gráficos: mayor resolución al capturar, un poco más de tiempo. El diseño del gráfico no cambia."
              >
                <input
                  type="checkbox"
                  checked={useUltraHdCharts}
                  onChange={(e) => setUseUltraHdCharts(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Alta calidad en gráficos</span>
              </label>
            </div>
          </div>
        </div>

        {/* Información del estudiante seleccionado */}
        {selectedStudentIds.length > 0 && (
          <div className="mt-4 bg-blue-50 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              <strong>Estudiante seleccionado:</strong> {
                selectedStudentIds.map(id => {
                  const student = findStudentById(filteredStudents, id);
                  return student ? `${student.name} (${student.course})` : '';
                }).join(', ')
              }
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Elige exportar con o sin descripción cualitativa. El PDF incluye portada, gráfico, descripción cualitativa (opcional) y descripción de subdimensiones.
            </p>
          </div>
        )}
      </div>

      {/* Modal de Vista Previa */}
      {showPreview && selectedStudentForPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            {/* Header del Modal */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <div className="flex items-center space-x-3">
                <FaEye className="text-blue-600 text-xl" />
                <h2 className="text-xl font-bold text-gray-800">Vista Previa del Informe</h2>
              </div>
              <div className="flex items-center flex-wrap gap-2">
                <button
                  onClick={() => handleExportFromPreview(true)}
                  disabled={isExportingPDF}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isExportingPDF ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      <span>Procesando PDF...</span>
                    </>
                  ) : (
                    <>
                      <FaFilePdf />
                      <span>Exportar PDF con descripción cualitativa</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleExportFromPreview(false)}
                  disabled={isExportingPDF}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isExportingPDF ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      <span>Procesando PDF...</span>
                    </>
                  ) : (
                    <>
                      <FaFilePdf />
                      <span>Exportar PDF sin descripción cualitativa</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
            
            {/* Contenido del Preview */}
            <div className="p-4">
              {renderStudentPreview(selectedStudentForPreview)}
            </div>
          </div>
        </div>
      )}

      {/* chartRef posicionado fuera de la vista para captura de PDF - CON TRANSFORMACIÓN IDÉNTICA AL VISIBLE */}
      {selectedStudentIds.length > 0 && chartData.length > 0 && (() => {
        // Recalcular la transformación para el gráfico oculto (EXACTAMENTE igual que el visible)
        let minAge = BASE_MIN_AGE, maxAge = BASE_MAX_AGE;
        let ageTicks: number[] = [];
        let ageMap: { [key: number]: string } = {};
        let offsetValue = 0;

        if (selectedStudentIds.length > 0) {
          const primaryStudentId = selectedStudentIds[0];
          const student = findStudentById(filteredStudents, primaryStudentId);
          if (student) {
            const anchoredAge = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
            const range = getSixYearRangeForAge(anchoredAge);
            minAge = range.minAge;
            maxAge = range.maxAge;
            const ticksAndLabels = createAgeTicksAndLabels(minAge, maxAge);
            ageTicks = ticksAndLabels.ticks;
            ageMap = ticksAndLabels.labels; // Para PDF usamos "X años"
            offsetValue = chartOffsetForMinAge(minAge);
            console.log('🔍 PDF Rango Dinámico Unificado:', { studentName: student.name, anchoredAge, minAge, maxAge });
          }
        }
        
        // Transformar datos igual que el gráfico visible
        const transformedChartData = chartData.map(item => ({
          ...item,
          percentageAchieved: Math.max(0, item.percentageAchieved - offsetValue),
          expectedLevel: Math.max(0, item.expectedLevel - offsetValue),
          previousYearLevel: Math.max(0, item.previousYearLevel - offsetValue)
        }));
        
        return (
          <div 
            ref={chartRef}
            data-chart-ref="true"
            className="fixed -left-[3000px] -top-[3000px] bg-white z-[-1]"
            style={{ 
              width: '1200px',  // Ancho optimizado (coincide con captureWidth)
              height: '900px',  // Alto optimizado (coincide con captureHeight)
              position: 'fixed',
              left: '-2000px',  // Suficiente para evitar interferencias
              top: '-2000px',   // Suficiente para evitar interferencias
              zIndex: -1,
              backgroundColor: 'white',
              overflow: 'hidden',
              transform: 'scale(1)', // Asegurar escala normal
              transformOrigin: 'top left'
            }}
          >
            <div className="w-full h-full">
              <StudentDimensionsChart 
                student={findStudentById(students, selectedStudentIds[0]) ?? students[0]}
                showLegend={true}
                height={900}
                pdfMode={true}
                showSubdimensions={true}
                tenantKey={tenantKey}
              dimensionCategories={filteredDimensionCategories}
              showPreviousYearLevel={true}
              />
            </div>
          </div>
        );
      })()}

      {/* Lista de estudiantes simplificada */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center">
          <FaUsers className="mr-2 text-indigo-600" />
          Estudiantes Disponibles
          {selectedStudentIds.length > 0 && (
            <span className="ml-3 text-sm bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
              {selectedStudentIds.length} seleccionado{selectedStudentIds.length > 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStudents.map((student) => {
            const isSelected = student.id && selectedStudentIds.some(sel => studentIdsMatch(sel, student.id));
            
            return (
              <div
                key={student.id}
                onClick={(event) => handleStudentSelect(event, student.id as any)}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-50 shadow-md ring-2 ring-indigo-200'
                    : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold transition-all duration-200 ${
                    isSelected
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-700 scale-110'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600'
                  }`}>
                    {student.name.charAt(0)}
                  </div>
                  <div className="flex space-x-1">
                    {isSelected && (
                      <div className="flex items-center text-indigo-600">
                        <FaCheck className="text-lg" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewStudent(student);
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-blue-100 hover:bg-blue-200 text-blue-600'
                      }`}
                      title="Vista previa del informe"
                    >
                      <FaEye />
                    </button>
                  </div>
                </div>
                <div>
                  <h4 className={`font-semibold transition-colors ${
                    isSelected ? 'text-indigo-800' : 'text-gray-800'
                  }`}>
                    {student.name}
                  </h4>
                  <p className={`text-sm transition-colors ${
                    isSelected ? 'text-indigo-600' : 'text-gray-600'
                  }`}>
                    {student.course}
                  </p>
                  <p className="text-xs text-gray-500">{calculateAge(student.birthDate)}</p>
                </div>
                {isSelected && (
                  <div className="mt-2 text-xs text-indigo-600 font-medium">
                    ✓ Listo para exportar
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de selección de cursos para exportación masiva */}
      {showCourseSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Exportación Masiva por Cursos</h3>
                  <p className="text-gray-600 mt-1">
                    Selecciona los cursos que deseas exportar. Los PDFs se organizarán en carpetas por curso dentro de un archivo ZIP.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCourseSelectionModal(false);
                    setSelectedCoursesForExport([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>

              {/* Controles de selección */}
              <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800">
                  Cursos Disponibles ({courses.length})
                </h4>
                <button
                  onClick={handleSelectAllCoursesForExport}
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {selectedCoursesForExport.length === courses.length ? (
                    <>
                      <FaTimes size={16} />
                      <span>Deseleccionar Todos</span>
                    </>
                  ) : (
                    <>
                      <FaCheck size={16} />
                      <span>Seleccionar Todos</span>
                    </>
                  )}
                </button>
              </div>

              {/* Grid de cursos */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {courses.map(course => {
                  const studentsInCourse = students.filter(s => s.course === course).length;
                  const isSelected = selectedCoursesForExport.includes(course);
                  
                  return (
                    <div
                      key={course}
                      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 shadow-sm' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => handleCourseToggleForExport(course)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <FaCheck className="text-white" size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{course}</p>
                          <p className="text-sm text-gray-500">{studentsInCourse} estudiantes</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Resumen de selección */}
              {selectedCoursesForExport.length > 0 && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">
                    {selectedCoursesForExport.length} cursos seleccionados • {
                      students.filter(s => selectedCoursesForExport.includes(s.course)).length
                    } estudiantes
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    Cursos: {selectedCoursesForExport.join(', ')}
                  </p>
                </div>
              )}

              {/* Modo de exportación */}
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Modo de exportación:</strong>{' '}
                  {massExportIncludeQualitative
                    ? 'PDFs con descripción cualitativa (portada, gráfico, descripción cualitativa y descripción de subdimensiones).'
                    : 'PDFs sin descripción cualitativa (portada, gráfico y descripción de subdimensiones).'}
                </p>
              </div>

              {/* Botones de acción */}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowCourseSelectionModal(false);
                    setSelectedCoursesForExport([]);
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isExportingMassive}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => exportPDFMassiveByCourseFolders(massExportIncludeQualitative)}
                  disabled={isExportingMassive || selectedCoursesForExport.length === 0}
                  className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg flex items-center space-x-3 ${
                    isExportingMassive || selectedCoursesForExport.length === 0
                      ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transform hover:scale-105'
                  }`}
                >
                  {isExportingMassive ? (
                    <>
                      <FaSpinner className="animate-spin" size={20} />
                      <span>Exportando...</span>
                    </>
                  ) : (
                    <>
                      <FaDownload size={20} />
                      <span>
                        {massExportIncludeQualitative
                          ? 'Exportar PDFs por cursos con descripción cualitativa (ZIP)'
                          : 'Exportar PDFs por cursos sin descripción cualitativa (ZIP)'}
                      </span>
                    </>
                  )}
                </button>
              </div>

              {/* Panel de progreso mejorado */}
              {isExportingMassive && (
                <div className="mt-6 p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  {/* Header del progreso */}
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        exportState.isPaused ? 'bg-yellow-500 animate-pulse' : 
                        exportState.isRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                      }`}></div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {exportState.isPaused ? 'Exportación Pausada' : 
                         exportState.isRunning ? 'Exportando PDFs...' : 'Exportación Detenida'}
                      </h3>
                    </div>
                    
                    {/* Controles de pausa/detener */}
                    <div className="flex space-x-2">
                      <button
                        onClick={togglePauseExport}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                          exportState.isPaused 
                            ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100' 
                            : 'bg-yellow-50 border-yellow-300 text-yellow-700 hover:bg-yellow-100'
                        }`}
                        disabled={!exportState.isRunning}
                      >
                        {exportState.isPaused ? <FaPlay size={16} /> : <FaPause size={16} />}
                        <span className="text-sm font-medium">
                          {exportState.isPaused ? 'Reanudar' : 'Pausar'}
                        </span>
                      </button>
                      
                      <button
                        onClick={stopExport}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-50 border border-red-300 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                        disabled={!exportState.isRunning}
                      >
                        <FaStopCircle size={16} />
                        <span className="text-sm font-medium">Detener</span>
                      </button>
                    </div>
                  </div>

                  {/* Barra de progreso principal */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Progreso General</span>
                      <span>{Math.round(exportProgress)}%</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-4 mb-2">
                      <div 
                        className={`h-4 rounded-full transition-all duration-500 ${
                          exportState.isPaused ? 'bg-yellow-500' : 'bg-blue-600'
                        }`}
                      style={{ width: `${exportProgress}%` }}
                    ></div>
                  </div>
                  </div>

                  {/* Estadísticas detalladas */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{exportState.processedCount}</div>
                      <div className="text-xs text-blue-500">Procesados</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-600">{exportState.totalCount}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{exportState.errorCount}</div>
                      <div className="text-xs text-red-500">Errores</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{sessionStatus.refreshCount}</div>
                      <div className="text-xs text-green-500">Token Refresh</div>
                    </div>
                  </div>

                  {/* Información de tiempo */}
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <FaClock className="text-gray-400" />
                      <span>
                        Tiempo transcurrido: {
                          Math.floor((Date.now() - (exportState.startTime || Date.now())) / 1000)
                        }s
                      </span>
                    </div>
                    
                    {exportState.estimatedTimeRemaining > 0 && !exportState.isPaused && (
                      <div className="flex items-center space-x-2">
                        <span>Tiempo estimado restante: {Math.floor(exportState.estimatedTimeRemaining / 1000)}s</span>
                      </div>
                    )}
                  </div>

                  {/* Estado de renovación de token */}
                  {sessionStatus.isRefreshing && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-yellow-800">
                        <FaSpinner className="animate-spin" />
                        <span className="text-sm">Renovando token de sesión...</span>
                      </div>
                    </div>
                  )}

                  {/* Lista de errores (si hay) */}
                  {exportState.errors.length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">Errores encontrados:</h4>
                      <div className="max-h-32 overflow-y-auto">
                        {exportState.errors.slice(-5).map((error, index) => (
                          <p key={index} className="text-sm text-red-700 mb-1">• {error}</p>
                        ))}
                        {exportState.errors.length > 5 && (
                          <p className="text-xs text-red-600 mt-2">
                            ... y {exportState.errors.length - 5} errores más
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tutorial ahora manejado por TutorialSection */}

      </div>
    </SimpleTutorialSection>
  );
};

export default VisualizacionResultados;
